import React, { useState, useEffect, useMemo } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import {
  Layout, Menu, Card, Row, Col, Statistic, Table, Tag, Button, Modal,
  Form, Input, InputNumber, Switch, Select, message, Tabs, Typography,
  Space, Badge, Descriptions, Popconfirm, Tooltip, Divider, Alert, Drawer,
  Pagination, ColorPicker, Progress
} from 'antd';
import {
  DashboardOutlined, TeamOutlined, HeartOutlined, SafetyOutlined,
  SettingOutlined, ApiOutlined, QuestionCircleOutlined, SyncOutlined,
  FileTextOutlined, BellOutlined, TrophyOutlined,
  MessageOutlined, KeyOutlined, LinkOutlined, ExportOutlined,
  SendOutlined, HistoryOutlined, InfoCircleOutlined, ClearOutlined,
  TagsOutlined, PlusOutlined, DeleteOutlined, EditOutlined, BgColorsOutlined,
  CheckOutlined, CloseOutlined, LoadingOutlined, SolutionOutlined, HolderOutlined
} from '@ant-design/icons';

import {
  DndContext, closestCenter, KeyboardSensor,
  PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import './App.css';
import ColumnMenu from './components/ColumnMenu';
import ColumnPicker from './components/ColumnPicker';
import { createTableFiltersStore } from './store/createTableFiltersStore';

const { Header, Content, Sider } = Layout;
const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;
const { TextArea } = Input;

// E-Commerce Club join form template (10 fields + 4 dictionaries from their CSVs)
const ECOMMERCE_CLUB_JOIN_FIELDS = [
  { id: 'ec_full_name', label: 'ФИО', type: 'text', required: true, placeholder: 'Иванов Иван Иванович' },
  { id: 'ec_position', label: 'Должность', type: 'text', required: true, placeholder: 'Укажите должность' },
  {
    id: 'ec_position_level', label: 'Уровень позиции', type: 'radio', required: true, placeholder: '',
    options: ['TOP', 'Upper-middle', 'Middle', 'Specialist', 'Junior', 'Владелец'],
  },
  { id: 'ec_company', label: 'Название компании', type: 'text', required: true, placeholder: 'Укажите место работы' },
  { id: 'ec_corp_email', label: 'Корпоративный email', type: 'email', required: true, placeholder: 'company@domain.ru' },
  { id: 'ec_phone', label: 'Телефон', type: 'tel', required: true, placeholder: '+7 (000) 000 00 00' },
  {
    id: 'ec_function', label: 'Функция', type: 'select', required: true, placeholder: '',
    options: [
      'Высшее руководство',
      'Продажи и развитие бизнеса',
      'Marketing/PR и Digital',
      'Большие данные/аналитика',
      'Разработка и программирование',
      'Тестирование',
      'Управление цепями поставок/закупки',
      'Управление персоналом',
      'Производство и инжиниринг',
      'Финансы и бухгалтерия',
      'Дизайн',
      'Юриспруденция',
    ],
  },
  {
    id: 'ec_business_segment', label: 'Сегмент бизнеса', type: 'select', required: true, placeholder: '',
    options: ['Ритейл', 'Бренд/производство', 'Дистрибуция', 'Селлер', 'Сервис', 'Агентство'],
  },
  {
    id: 'ec_industry', label: 'Индустрия', type: 'radio', required: true, placeholder: '',
    options: [
      'FMCG', 'Fashion', 'Красота', 'Электроника', 'Бытовая техника',
      'Luxury', 'Ювелирная', 'Фарма', 'IT/Telecom', 'Агро',
      'Промышленное оборудование', 'Свой вариант',
    ],
    follow_up_field: {
      id: 'ec_industry_other',
      trigger_value: 'Свой вариант',
      label: 'Уточните индустрию',
      type: 'text',
      required: true,
      placeholder: 'Например: Складская логистика',
    },
  },
  { id: 'ec_skills', label: 'Профессиональный стек/сервисы', type: 'textarea', required: false, placeholder: 'Например, CRM, 1C-Bitrix и т.д.' },
];


function SortableRow({ id, ...props }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    ...props.style,
    transform: CSS.Transform.toString(transform && { ...transform, scaleY: 1 }),
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 9999, background: '#fafafa' } : {}),
  };
  return <tr {...props} ref={setNodeRef} style={style} {...attributes} {...listeners} />;
}

// API configuration
const API_BASE = process.env.REACT_APP_API_URL || '';

// Auth context
const AuthContext = React.createContext(null);

// Login form
function LoginForm({ onLogin }) {
  const [loading, setLoading] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!username || !password) {
      setError('Введите логин и пароль');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        localStorage.setItem('apiKey', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        message.success(`Добро пожаловать, ${data.user?.name || data.user?.email || 'Admin'}!`);
        onLogin(data.token);
      } else {
        setError(data.error || data.detail || 'Ошибка входа');
      }
    } catch (e) {
      setError('Сервис временно недоступен, попробуйте позже');
    }
    setLoading(false);
  };

  return (
    <div style={{ 
      height: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
    }}>
      <Card 
        title={
          <div style={{ textAlign: 'center', fontSize: 20 }}>
            ☕ Random Coffee Admin
          </div>
        } 
        style={{ width: 400, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
      >
        <Form layout="vertical" onFinish={handleSubmit}>
          <Form.Item label="Логин" required>
            <Input
              prefix={<TeamOutlined />}
              placeholder="Введите логин"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              size="large"
            />
          </Form.Item>
          <Form.Item label="Пароль" required>
            <Input.Password
              prefix={<SafetyOutlined />}
              placeholder="Введите пароль"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              size="large"
            />
          </Form.Item>
          
          {error && (
            <Alert
              type="error"
              message={error}
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
          
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              block
              size="large"
            >
              Войти
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
}

// Help drawer
function HelpDrawer({ visible, onClose }) {
  return (
    <Drawer
      title="📖 Справка по работе с системой"
      placement="right"
      width={600}
      onClose={onClose}
      open={visible}
    >
      <Title level={4}>☕ Random Coffee System</Title>
      <Paragraph>
        Система для регулярных знакомств и нетворкинга внутри сообщества в Telegram.
      </Paragraph>

      <Divider />

      <Title level={5}>🚀 Как начать работу</Title>
      <ol>
        <li>Создайте бота через <a href="https://t.me/BotFather" target="_blank" rel="noreferrer">@BotFather</a></li>
        <li>Получите токен бота и добавьте его в файл <code>.env</code></li>
        <li>Добавьте бота в ваш чат и сделайте его администратором</li>
        <li>Узнайте ID чата и добавьте в <code>.env</code> как <code>COMMUNITY_CHAT_ID</code></li>
        <li>Перезапустите бота: <code>docker-compose restart bot</code></li>
      </ol>

      <Divider />

      <Title level={5}>👥 Как работает рандом-кофе</Title>
      <ol>
        <li><b>Онбординг:</b> Пользователь нажимает /start в боте</li>
        <li><b>Верификация:</b> Бот проверяет, что пользователь в чате</li>
        <li><b>Анкета:</b> Пользователь заполняет профиль</li>
        <li><b>Опт-ин:</b> Пользователь соглашается на участие</li>
        <li><b>Матч:</b> Раз в неделю бот подбирает пары</li>
        <li><b>Встреча:</b> Пара связывается и встречается</li>
        <li><b>Фидбек:</b> Бот собирает обратную связь</li>
      </ol>

      <Divider />

      <Title level={5}>⭐ Система кармы</Title>
      <Paragraph>
        В чате работают благодарности:
      </Paragraph>
      <ul>
        <li><code>/thanks @username причина</code> — начислить карму</li>
        <li>Ответить на сообщение с <code>/thanks</code> — начислить автору</li>
      </ul>

      <Divider />

      <Title level={5}>⚠️ Модерация</Title>
      <Paragraph>
        Система страйков за нарушения:
      </Paragraph>
      <ul>
        <li><code>/report</code> (ответом) — пожаловаться на сообщение</li>
        <li>Модератор подтверждает страйк в админке</li>
        <li>При достижении порога — мьют или бан</li>
      </ul>

      <Divider />

      <Title level={5}>📊 Страницы админки</Title>
      <Descriptions column={1} size="small">
        <Descriptions.Item label="Дашборд">Общая статистика системы</Descriptions.Item>
        <Descriptions.Item label="Пользователи">Список всех участников</Descriptions.Item>
        <Descriptions.Item label="Матчи">История циклов матчинга</Descriptions.Item>
        <Descriptions.Item label="Карма">Лидерборд и история</Descriptions.Item>
        <Descriptions.Item label="Модерация">Очередь страйков</Descriptions.Item>
        <Descriptions.Item label="Настройки">Все параметры системы</Descriptions.Item>
      </Descriptions>

      <Divider />

      <Title level={5}>⚙️ Настройки</Title>
      <Paragraph>
        Все параметры системы можно изменить на странице "Настройки". 
        Изменения сохраняются в базу данных и применяются немедленно.
      </Paragraph>

      <Alert
        type="warning"
        message="Важно"
        description="После изменения настроек может потребоваться перезапуск worker для применения расписания."
        showIcon
      />
    </Drawer>
  );
}

// Dashboard page
function DashboardPage({ apiKey, communityId }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const params = communityId ? `?community_id=${communityId}` : '';
      const response = await fetch(`${API_BASE}/api/dashboard${params}`, {
        headers: { 'Authorization': `Bearer ${apiKey}` }
      });
      if (response.ok) {
        setStats(await response.json());
      }
    } catch (e) {
      message.error('Не удалось загрузить статистику');
    }
    setLoading(false);
  };

  useEffect(() => { fetchStats(); }, [communityId]);

  if (loading) return <Card loading={true} />;

  return (
    <div>
      <Title level={3}>📊 Дашборд</Title>
      
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card>
            <Statistic 
              title={
                <Tooltip title="Все пользователи, которые хотя бы раз запустили бота командой /start">
                  <span>Всего пользователей <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} /></span>
                </Tooltip>
              } 
              value={stats?.users?.total || 0} 
              prefix={<TeamOutlined />} 
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card>
            <Statistic 
              title={
                <Tooltip title="Пользователи с активным статусом, не заблокированные и не на паузе">
                  <span>Активных <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} /></span>
                </Tooltip>
              } 
              value={stats?.users?.active || 0} 
              valueStyle={{ color: '#3f8600' }} 
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card>
            <Statistic 
              title={
                <Tooltip title="Пользователи, подтвердившие участие в вашем Telegram-чате (бот проверил их членство)">
                  <span>Верифицированных <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} /></span>
                </Tooltip>
              } 
              value={stats?.users?.verified || 0} 
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card>
            <Statistic 
              title={
                <Tooltip title="Пользователи, которые согласились на участие в еженедельном рандом-кофе (opt-in)">
                  <span>Участвуют в кофе <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} /></span>
                </Tooltip>
              } 
              value={stats?.users?.opted_in || 0} 
              valueStyle={{ color: '#1890ff' }} 
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card>
            <Statistic 
              title={
                <Tooltip title="Временно приостановили участие (пауза/снуз). Могут возобновить в любой момент">
                  <span>На паузе <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} /></span>
                </Tooltip>
              } 
              value={stats?.users?.paused || 0} 
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card>
            <Statistic 
              title={
                <Tooltip title="Пользователи, которые систематически не отвечают на матчи (3+ раза подряд). Автоматически исключены из матчинга до восстановления">
                  <span>Ghost <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} /></span>
                </Tooltip>
              } 
              value={stats?.users?.ghost || 0} 
              valueStyle={{ color: '#cf1322' }} 
            />
          </Card>
        </Col>
      </Row>

      <Divider />

      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Card title="☕ Random Coffee">
            <Row gutter={16}>
              <Col span={12}>
                <Statistic title="Циклов" value={stats?.matches?.total_cycles || 0} />
              </Col>
              <Col span={12}>
                <Statistic title="Матчей" value={stats?.matches?.total_matches || 0} />
              </Col>
              <Col span={12}>
                <Statistic title="Встреч" value={stats?.matches?.successful_meetups || 0} valueStyle={{ color: '#3f8600' }} />
              </Col>
              <Col span={12}>
                <Statistic title="Ожидают фидбек" value={stats?.matches?.pending_feedback || 0} />
              </Col>
            </Row>
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card title="⭐ Карма">
            <Row gutter={16}>
              <Col span={12}>
                <Statistic title="Всего событий" value={stats?.karma?.total_events || 0} />
              </Col>
              <Col span={12}>
                <Statistic title="Очков выдано" value={stats?.karma?.total_points_given || 0} />
              </Col>
            </Row>
            {stats?.karma?.top_users?.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <Text type="secondary">Топ участники:</Text>
                {stats.karma.top_users.map((u, i) => (
                  <Tag key={i} color={i === 0 ? 'gold' : i === 1 ? 'silver' : 'bronze'}>
                    {u.name} ({u.karma})
                  </Tag>
                ))}
              </div>
            )}
          </Card>
        </Col>
      </Row>

      <Divider />

      <Card title="📋 Последняя активность" extra={<Button icon={<SyncOutlined />} onClick={fetchStats}>Обновить</Button>}>
        {stats?.recent_activity?.length > 0 ? (
          <Table
            dataSource={stats.recent_activity}
            rowKey="id"
            size="small"
            columns={[
              { title: 'Тип', dataIndex: 'type', render: (t) => <Tag>{t}</Tag> },
              { title: 'Описание', dataIndex: 'description' },
              { title: 'Время', dataIndex: 'timestamp', render: (t) => new Date(t).toLocaleString('ru-RU') }
            ]}
          />
        ) : (
          <Alert type="info" message="Нет данных об активности" />
        )}
      </Card>
    </div>
  );
}

// Employer type labels
const EMPLOYER_TYPE_LABELS = {
  ad_agency: 'Рекламное агентство',
  seller: 'Продавец',
  ad_platform: 'Рекламная площадка',
  brand_representative: 'Представитель бренда',
  retailer_representative: 'Представитель ритейлера',
  it_service: 'IT-сервис',
  other: 'Другое',
};

// ─── Bulk actions definitions ───────────────────────────────
const BULK_ACTION_OPTIONS = [
  { value: 'assignTags', label: '🏷️ Присвоить теги' },
  { value: 'removeTags', label: '🗑️ Снять теги' },
  { value: 'sendMessage', label: '📨 Отправить сообщение' },
];

// Helper: check if click is on checkbox/selection column — skip row click
function shouldSkipRowAction(event) {
  const target = event.target;
  if (!target) return false;
  return Boolean(
    target.closest('.ant-table-selection-column') ||
    target.closest('.ant-checkbox') ||
    target.closest('.ant-checkbox-wrapper') ||
    target.closest('.ant-tag')
  );
}

// Helper: execute actions sequentially with progress
async function executeActionsSequentially(ids, action, { onProgress, delayMs = 100 } = {}) {
  const results = [];
  const total = ids.length;
  for (let i = 0; i < total; i++) {
    const id = ids[i];
    try {
      const value = await action(id);
      results.push({ id, status: 'fulfilled', value });
    } catch (error) {
      results.push({ id, status: 'rejected', error });
    }
    if (onProgress) onProgress(i + 1, total, id);
    if (delayMs > 0 && i < total - 1) {
      await new Promise(resolve => setTimeout(resolve, delayMs));
    }
  }
  return results;
}

// Create users table filters store
const useUsersFiltersStore = createTableFiltersStore({
  tableId: 'users',
  initialSort: 'id',
  initialLimit: '20',
  limitOptions: ['20', '50', '100'],
  initialColumns: [
    { id: 'id', label: 'ID', visible: true },
    { id: 'telegram_user_id', label: 'Telegram ID', visible: false },
    { id: 'username', label: 'Username', visible: true },
    { id: 'first_name', label: 'Имя', visible: true },
    { id: 'status', label: 'Статус', visible: true },
    { id: 'company', label: 'Компания', visible: true },
    { id: 'position', label: 'Должность', visible: true },
    { id: 'employer_type', label: 'Тип работодателя', visible: false },
    { id: 'city', label: 'Город', visible: false },
    { id: 'meeting_goals', label: 'Цели встреч', visible: false },
    { id: 'opt_in_coffee', label: 'Кофе', visible: true },
    { id: 'karma_total', label: 'Карма', visible: false },
    { id: 'strikes_total', label: 'Страйки', visible: false },
    { id: 'ghost_status', label: 'Ghost', visible: false },
    { id: 'created_at', label: 'Первое сообщение', visible: false },
    { id: 'profile_completed_at', label: 'Анкета заполнена', visible: true },
    { id: 'onboarded_at', label: 'Подтверждён', visible: false },
    { id: 'bot_can_write', label: 'Бот может писать', visible: true },
    { id: 'first_bot_interaction_at', label: 'Первое обращение к боту', visible: false },
    { id: 'last_bot_interaction_at', label: 'Последнее обращение к боту', visible: false },
    { id: 'tags', label: 'Теги', visible: true },
    { id: 'last_activity_at', label: 'Последняя активность', visible: false },
  ],
});

// Column definitions for users table
const USERS_COLUMN_DEFS = {
  id: { fieldType: 'number' },
  telegram_user_id: { fieldType: 'text' },
  username: { fieldType: 'text' },
  first_name: { fieldType: 'text' },
  status: { fieldType: 'single_choice', options: ['active', 'paused', 'blocked'] },
  company: { fieldType: 'text' },
  position: { fieldType: 'text' },
  employer_type: { fieldType: 'single_choice', options: Object.keys(EMPLOYER_TYPE_LABELS) },
  city: { fieldType: 'text' },
  meeting_goals: { fieldType: 'text' },
  opt_in_coffee: { fieldType: 'boolean' },
  karma_total: { fieldType: 'number' },
  strikes_total: { fieldType: 'number' },
  ghost_status: { fieldType: 'boolean' },
  created_at: { fieldType: 'date' },
  profile_completed_at: { fieldType: 'date' },
  onboarded_at: { fieldType: 'date' },
  bot_can_write: { fieldType: 'boolean' },
  first_bot_interaction_at: { fieldType: 'date' },
  last_bot_interaction_at: { fieldType: 'date' },
  tags: { fieldType: 'text' },
  last_activity_at: { fieldType: 'date' },
};

// User detail drawer with tag management
function UserDetailDrawer({ userId, apiKey, visible, onClose, allTags, onTagsChanged, communityId }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userTags, setUserTags] = useState([]);
  const [newTagName, setNewTagName] = useState('');
  const [creatingTag, setCreatingTag] = useState(false);

  const fetchUser = () => {
    if (userId && visible) {
      setLoading(true);
      const qs = communityId ? `?community_id=${communityId}` : '';
      fetch(`${API_BASE}/api/users/${userId}${qs}`, {
        headers: { 'Authorization': `Bearer ${apiKey}` },
      })
        .then(r => r.json())
        .then(data => { setUser(data); setLoading(false); })
        .catch(() => setLoading(false));
    }
  };

  const fetchUserTags = () => {
    if (userId && visible) {
      fetch(`${API_BASE}/api/tags/user/${userId}`, {
        headers: { 'Authorization': `Bearer ${apiKey}` },
      })
        .then(r => r.json())
        .then(data => setUserTags(Array.isArray(data) ? data : []))
        .catch(() => setUserTags([]));
    }
  };

  useEffect(() => { fetchUser(); fetchUserTags(); }, [userId, visible, apiKey]);

  const handleAssignTag = async (tagId) => {
    try {
      await fetch(`${API_BASE}/api/tags/user/${userId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${apiKey}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ tag_ids: [tagId] }),
      });
      fetchUserTags();
      if (onTagsChanged) onTagsChanged();
    } catch (e) {
      message.error('Ошибка при назначении тега');
    }
  };

  const handleRemoveTag = async (tagId) => {
    try {
      await fetch(`${API_BASE}/api/tags/user/${userId}/${tagId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${apiKey}` },
      });
      fetchUserTags();
      if (onTagsChanged) onTagsChanged();
    } catch (e) {
      message.error('Ошибка при снятии тега');
    }
  };

  const handleQuickCreate = async () => {
    if (!newTagName.trim()) return;
    setCreatingTag(true);
    try {
      const body = { name: newTagName.trim(), color: '#2db7f5' };
      if (communityId) body.community_id = communityId;
      const res = await fetch(`${API_BASE}/api/tags`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${apiKey}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const tag = await res.json();
        message.success(`Тег «${tag.name}» создан`);
        setNewTagName('');
        await handleAssignTag(tag.id);
        if (onTagsChanged) onTagsChanged();
      } else {
        const err = await res.json();
        message.error(err.detail || 'Ошибка');
      }
    } catch (e) {
      message.error('Ошибка сети');
    }
    setCreatingTag(false);
  };

  const goalLabels = user?.profile?.meeting_goals_labels || user?.profile?.meeting_goals || [];
  const userTagIds = userTags.map(t => t.id);
  const availableTags = (allTags || []).filter(t => !userTagIds.includes(t.id));

  return (
    <Drawer title="Профиль участника" open={visible} onClose={onClose} width={520} loading={loading}>
      {user && (
        <>
          {/* Tags section — right at the top for visibility */}
          <div style={{ marginBottom: 16 }}>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>Теги:</Text>
            <Space wrap size={4} style={{ marginBottom: 8 }}>
              {userTags.map(t => (
                <Tag
                  key={t.id}
                  color={t.color}
                  closable
                  onClose={() => handleRemoveTag(t.id)}
                  style={{ fontSize: 13 }}
                >
                  {t.name}
                </Tag>
              ))}
              {userTags.length === 0 && <Text type="secondary">Нет тегов</Text>}
            </Space>

            {availableTags.length > 0 && (
              <div style={{ marginBottom: 8 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>Добавить:</Text>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                  {availableTags.map(t => (
                    <Tag
                      key={t.id}
                      style={{ cursor: 'pointer', borderStyle: 'dashed', opacity: 0.7 }}
                      onClick={() => handleAssignTag(t.id)}
                    >
                      <PlusOutlined style={{ fontSize: 10 }} /> {t.name}
                    </Tag>
                  ))}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: 8 }}>
              <Input
                size="small"
                placeholder="Новый тег..."
                value={newTagName}
                onChange={e => setNewTagName(e.target.value)}
                onPressEnter={handleQuickCreate}
                style={{ width: 180 }}
              />
              <Button size="small" type="primary" icon={<PlusOutlined />} onClick={handleQuickCreate} loading={creatingTag}>
                Создать
              </Button>
            </div>
          </div>

          <Divider style={{ margin: '8px 0 16px' }} />

          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Telegram ID">{user.telegram_user_id}</Descriptions.Item>
            <Descriptions.Item label="Username">{user.username ? `@${user.username}` : '—'}</Descriptions.Item>
            <Descriptions.Item label="Статус"><Tag color={user.status === 'active' ? 'green' : 'orange'}>{user.status}</Tag></Descriptions.Item>
            <Descriptions.Item label="Random Coffee"><Tag color={user.opt_in_coffee ? 'blue' : 'default'}>{user.opt_in_coffee ? 'Участвует' : 'Нет'}</Tag></Descriptions.Item>
            <Descriptions.Item label="Карма">{user.karma_total}</Descriptions.Item>
            <Descriptions.Item label="Страйки">{user.strikes_total}</Descriptions.Item>
            <Descriptions.Item label="Успешных встреч">{user.successful_meetups}</Descriptions.Item>
          </Descriptions>

          {user.profile && (
            <>
              <Divider>Анкета</Divider>
              <Descriptions bordered column={1} size="small">
                <Descriptions.Item label="Имя">{user.profile.first_name || user.profile.full_name || '—'}</Descriptions.Item>
                <Descriptions.Item label="Фамилия">{user.profile.last_name || '—'}</Descriptions.Item>
                <Descriptions.Item label="Телефон">{user.profile.phone || '—'}</Descriptions.Item>
                <Descriptions.Item label="Email">{user.profile.email || '—'}</Descriptions.Item>
                <Descriptions.Item label="Город">{user.profile.city || '—'}</Descriptions.Item>
                <Descriptions.Item label="О себе">{user.profile.bio || '—'}</Descriptions.Item>
              </Descriptions>
            </>
          )}

          {user.jobs && user.jobs.length > 0 && (
            <>
              <Divider>Места работы</Divider>
              {user.jobs.map((j, idx) => (
                <Card key={j.id || idx} size="small" style={{ marginBottom: 8 }}>
                  <p><b>{j.position}</b> в <b>{j.company}</b></p>
                  <p>Тип: {EMPLOYER_TYPE_LABELS[j.employer_type] || j.employer_type || '—'} {j.is_primary && <Tag color="blue">Основное</Tag>}</p>
                </Card>
              ))}
            </>
          )}

          {goalLabels.length > 0 && (
            <>
              <Divider>Цели встреч</Divider>
              <Space wrap>
                {goalLabels.map((g, i) => <Tag key={i} color="geekblue">{g}</Tag>)}
              </Space>
            </>
          )}

          {user.join_form_data && Object.keys(user.join_form_data).length > 0 && (
            <>
              <Divider>Анкета на вступление</Divider>
              <Descriptions bordered column={1} size="small">
                {Object.entries(user.join_form_data).map(([key, val]) => (
                  <Descriptions.Item key={key} label={key}>
                    {val || <Text type="secondary">—</Text>}
                  </Descriptions.Item>
                ))}
              </Descriptions>
            </>
          )}
        </>
      )}
    </Drawer>
  );
}


// Feedbacks page
function FeedbacksPage({ apiKey, communityId }) {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchFeedbacks = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (communityId) params.set('community_id', communityId);
      const response = await fetch(`${API_BASE}/api/feedbacks?${params}`, {
        headers: { 'Authorization': `Bearer ${apiKey}` },
      });
      if (response.ok) setFeedbacks(await response.json());
    } catch (e) {
      message.error('Не удалось загрузить отзывы');
    }
    setLoading(false);
  };

  useEffect(() => { fetchFeedbacks(); }, [communityId]);

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: 'Участник', dataIndex: 'user_name', key: 'user_name', render: v => v || '—' },
    {
      title: 'Статус', dataIndex: 'status', key: 'status',
      render: s => {
        const colors = { met: 'green', rescheduled: 'orange', no_contact: 'red', ghosted: 'volcano', pending: 'default', declined: 'red' };
        return <Tag color={colors[s] || 'default'}>{s}</Tag>;
      }
    },
    {
      title: 'Оценка', dataIndex: 'rating', key: 'rating',
      render: r => r ? '⭐'.repeat(r) : '—',
    },
    {
      title: 'Полезно', dataIndex: 'was_useful', key: 'was_useful',
      render: v => v === true ? <Tag color="green">Да</Tag> : v === false ? <Tag color="red">Нет</Tag> : '—',
    },
    {
      title: 'Встретиться снова', dataIndex: 'would_meet_again', key: 'would_meet_again',
      render: v => v === true ? <Tag color="green">Да</Tag> : v === false ? <Tag color="red">Нет</Tag> : '—',
    },
    {
      title: 'Комментарий', dataIndex: 'comment', key: 'comment', width: 250,
      render: v => v || '—',
    },
    {
      title: 'Дата', dataIndex: 'responded_at', key: 'responded_at',
      render: d => d ? new Date(d).toLocaleString('ru-RU') : '—',
    },
  ];

  const avgRating = feedbacks.filter(f => f.rating).length > 0
    ? (feedbacks.filter(f => f.rating).reduce((s, f) => s + f.rating, 0) / feedbacks.filter(f => f.rating).length).toFixed(1)
    : '—';
  const usefulPct = feedbacks.filter(f => f.was_useful !== null).length > 0
    ? Math.round(feedbacks.filter(f => f.was_useful === true).length / feedbacks.filter(f => f.was_useful !== null).length * 100)
    : '—';

  return (
    <div>
      <Title level={3}>💬 Обратная связь</Title>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card><Statistic title="Всего отзывов" value={feedbacks.length} /></Card>
        </Col>
        <Col span={6}>
          <Card><Statistic title="Средняя оценка" value={avgRating} suffix="/ 5" /></Card>
        </Col>
        <Col span={6}>
          <Card><Statistic title="Полезных встреч" value={usefulPct} suffix="%" /></Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="Встретились" value={feedbacks.filter(f => f.status === 'met').length} />
          </Card>
        </Col>
      </Row>
      <Card>
        <Button icon={<SyncOutlined />} onClick={fetchFeedbacks} style={{ marginBottom: 16 }}>Обновить</Button>
        <Table dataSource={feedbacks} columns={columns} rowKey="id" loading={loading} pagination={{ pageSize: 20 }} />
      </Card>
    </div>
  );
}


// Users page with advanced filtering, tag management, bulk actions, and profile columns
function UsersPage({ apiKey, communityId }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [drawerUserId, setDrawerUserId] = useState(null);

  // Tag management state
  const [allTags, setAllTags] = useState([]);
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [assignUserId, setAssignUserId] = useState(null);
  const [createTagModalVisible, setCreateTagModalVisible] = useState(false);
  const [filterTagIds, setFilterTagIds] = useState([]);
  const [tagForm] = Form.useForm();
  const [sendMsgModalVisible, setSendMsgModalVisible] = useState(false);
  const [sendMsgText, setSendMsgText] = useState('');

  // ─── Bulk selection state (tg-crm pattern) ───────────────
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkAction, setBulkAction] = useState(null);
  const [isBulkRunning, setIsBulkRunning] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null);
  // NOTE: DO NOT add useEffect to filter selectedRowKeys when users change.
  // That would destroy selection across page changes.
  
  const {
    sort, rules, columns, page, limit,
    setSort, upsertRule, removeRule, setColumns, setPage, setLimit,
    getFiltersForQuery, getRuleForColumn,
  } = useUsersFiltersStore();

  const fetchTags = async () => {
    try {
      const params = new URLSearchParams();
      if (communityId) params.set('community_id', communityId);
      const res = await fetch(`${API_BASE}/api/tags?${params}`, {
        headers: { 'Authorization': `Bearer ${apiKey}` },
      });
      if (res.ok) setAllTags(await res.json());
    } catch (e) { /* ignore */ }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const filters = getFiltersForQuery();
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        sort: sort,
      });
      if (communityId) params.set('community_id', communityId);
      if (filterTagIds.length > 0) params.set('tag_ids', filterTagIds.join(','));
      filters.forEach(f => params.append('filter', f));
      
      const response = await fetch(`${API_BASE}/api/users?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${apiKey}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(Array.isArray(data) ? data : (data.items || []));
        setTotal(data.total || data.length || 0);
      }
    } catch (e) {
      setUsers([]);
    }
    setLoading(false);
  };

  useEffect(() => { fetchTags(); }, [communityId]);
  useEffect(() => { fetchUsers(); }, [page, limit, sort, rules, communityId, filterTagIds]);

  // ─── Tag assignment ──────────────────────────────────────
  const handleAssignTags = async (tagIds, userIds) => {
    setIsBulkRunning(true);
    setBulkProgress({ processed: 0, total: userIds.length });
    try {
      await executeActionsSequentially(
        userIds,
        async (uid) => {
          await fetch(`${API_BASE}/api/tags/user/${uid}`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${apiKey}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ tag_ids: tagIds }),
          });
        },
        {
          onProgress: (processed, total) => setBulkProgress({ processed, total }),
          delayMs: 100,
        }
      );
      message.success(`Теги назначены (${userIds.length} пользователей)`);
      setAssignModalVisible(false);
      setAssignUserId(null);
      setSelectedRowKeys([]);
      setBulkAction(null);
      fetchUsers();
      fetchTags();
    } catch (e) {
      message.error('Ошибка при назначении тегов');
    }
    setIsBulkRunning(false);
    setBulkProgress(null);
  };

  const handleRemoveTag = async (userId, tagId) => {
    try {
      await fetch(`${API_BASE}/api/tags/user/${userId}/${tagId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${apiKey}` },
      });
      message.success('Тег снят');
      fetchUsers();
      fetchTags();
    } catch (e) {
      message.error('Ошибка');
    }
  };

  const handleCreateTag = async (values) => {
    const color = typeof values.color === 'string' ? values.color
      : values.color?.toHexString ? values.color.toHexString()
      : '#2db7f5';
    const body = { name: values.name, color, description: values.description || '' };
    if (communityId) body.community_id = communityId;

    try {
      const res = await fetch(`${API_BASE}/api/tags`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${apiKey}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        message.success('Тег создан');
        setCreateTagModalVisible(false);
        tagForm.resetFields();
        fetchTags();
      } else {
        const err = await res.json();
        message.error(err.detail || 'Ошибка');
      }
    } catch (e) {
      message.error('Ошибка сети');
    }
  };

  // ─── Send DM handler ──────────────────────────────────────
  const handleSendDM = async () => {
    if (!sendMsgText.trim() || !communityId) return;
    setIsBulkRunning(true);
    setBulkProgress({ processed: 0, total: selectedRowKeys.length });
    setSendMsgModalVisible(false);

    try {
      const chunkSize = 20;
      let sent = 0;
      let failed = 0;
      for (let i = 0; i < selectedRowKeys.length; i += chunkSize) {
        const chunk = selectedRowKeys.slice(i, i + chunkSize);
        const res = await fetch(`${API_BASE}/api/actions/send-dm`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${apiKey}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_ids: chunk, message_text: sendMsgText, community_id: communityId }),
        });
        if (res.ok) {
          const result = await res.json();
          sent += result.sent || 0;
          failed += result.failed || 0;
        }
        setBulkProgress({ processed: Math.min(i + chunkSize, selectedRowKeys.length), total: selectedRowKeys.length });
      }
      message.success(`Отправлено: ${sent}, ошибок: ${failed}`);
      setSelectedRowKeys([]);
      setBulkAction(null);
      setSendMsgText('');
      fetchUsers();
    } catch (e) {
      message.error('Ошибка при отправке');
    }
    setIsBulkRunning(false);
    setBulkProgress(null);
  };

  // ─── Bulk action handler ─────────────────────────────────
  const handleBulkAction = () => {
    if (!bulkAction || !selectedRowKeys.length) return;

    if (bulkAction === 'assignTags') {
      setAssignUserId(null);
      setAssignModalVisible(true);
      return;
    }

    if (bulkAction === 'removeTags') {
      setAssignUserId(null);
      setAssignModalVisible(true);
      return;
    }

    if (bulkAction === 'sendMessage') {
      if (!communityId) {
        message.warning('Выберите сообщество для отправки сообщений');
        return;
      }
      setSendMsgText('');
      setSendMsgModalVisible(true);
      return;
    }
  };

  // Build columns with ColumnMenu
  const tableColumns = useMemo(() => {
    return columns
      .filter(col => col.visible)
      .map(col => {
        const def = USERS_COLUMN_DEFS[col.id] || { fieldType: 'text' };
        return {
          key: col.id,
          dataIndex: col.id,
          title: () => (
            <ColumnMenu
              columnId={col.id}
              label={col.label}
              fieldType={def.fieldType}
              options={def.options}
              rule={getRuleForColumn(col.id)}
              sort={sort}
              onSortChange={setSort}
              onApply={(slug, op, value) => upsertRule({ slug, operator: op, value })}
              onClear={removeRule}
            />
          ),
          render: col.id === 'username' ? (u) => u ? `@${u}` : '-' :
                  col.id === 'first_name' ? (v, rec) => {
                    const jd = rec.join_form_data || {};
                    const name = v || jd['Имя'] || '';
                    const surname = rec.last_name || jd['Фамилия'] || '';
                    return [surname, name].filter(Boolean).join(' ') || <span style={{ color: '#ccc' }}>—</span>;
                  } :
                  col.id === 'status' ? (s) => {
                    const colors = { active: 'green', paused: 'orange', blocked: 'red' };
                    return <Tag color={colors[s] || 'default'}>{s}</Tag>;
                  } :
                  col.id === 'employer_type' ? (v) => v ? <Tag>{EMPLOYER_TYPE_LABELS[v] || v}</Tag> : <span style={{ color: '#ccc' }}>—</span> :
                  col.id === 'company' ? (v, rec) => {
                    const val = v || (rec.join_form_data && (rec.join_form_data['Название компании'] || rec.join_form_data['Компания']));
                    return val || <span style={{ color: '#ccc' }}>—</span>;
                  } :
                  col.id === 'position' ? (v, rec) => {
                    const val = v || (rec.join_form_data && (rec.join_form_data['Должность']));
                    return val || <span style={{ color: '#ccc' }}>—</span>;
                  } :
                  col.id === 'city' ? (v) => v || <span style={{ color: '#ccc' }}>—</span> :
                  col.id === 'meeting_goals' ? (v) => v && v.length > 0
                    ? <Space wrap size={2}>{v.map((g, i) => <Tag key={i} color="geekblue" style={{ fontSize: 11 }}>{g}</Tag>)}</Space>
                    : <span style={{ color: '#ccc' }}>—</span> :
                  col.id === 'opt_in_coffee' ? (v) => <Tag color={v ? 'blue' : 'default'}>{v ? '✓' : '✗'}</Tag> :
                  col.id === 'ghost_status' ? (v) => v ? <Tag color="red">Да</Tag> : <Tag>Нет</Tag> :
                  col.id === 'strikes_total' ? (v) => v > 0 ? <Badge count={v} style={{ backgroundColor: '#ff4d4f' }} /> : 0 :
                  col.id === 'created_at' ? (d) => d ? new Date(d).toLocaleDateString('ru-RU') : '-' :
                  col.id === 'profile_completed_at' ? (d) => d ? <Tag color="green">{new Date(d).toLocaleDateString('ru-RU')}</Tag> : <Tag color="default">Нет</Tag> :
                  col.id === 'onboarded_at' ? (d) => d ? <Tag color="blue">{new Date(d).toLocaleDateString('ru-RU')}</Tag> : <Tag color="default">Нет</Tag> :
                  col.id === 'bot_can_write' ? (v) => v ? <Tag color="green">Да</Tag> : <Tag color="red">Нет</Tag> :
                  col.id === 'first_bot_interaction_at' ? (d) => d ? new Date(d).toLocaleDateString('ru-RU') : <span style={{ color: '#ccc' }}>—</span> :
                  col.id === 'last_bot_interaction_at' ? (d) => d ? new Date(d).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : <span style={{ color: '#ccc' }}>—</span> :
                  col.id === 'tags' ? (tags, record) => (
                    <Space wrap size={2}>
                      {tags && tags.map(t => (
                        <Tag
                          key={t.id}
                          color={t.color}
                          closable
                          onClose={(e) => { e.preventDefault(); e.stopPropagation(); handleRemoveTag(record.id, t.id); }}
                          style={{ marginBottom: 2 }}
                        >
                          {t.name}
                        </Tag>
                      ))}
                      <Tag
                        style={{ background: '#fff', borderStyle: 'dashed', cursor: 'pointer' }}
                        onClick={(e) => { e.stopPropagation(); setAssignUserId(record.id); setAssignModalVisible(true); }}
                      >
                        <PlusOutlined /> Тег
                      </Tag>
                    </Space>
                  ) :
                  col.id === 'last_activity_at' ? (d) => d ? new Date(d).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-' :
                  undefined,
        };
      });
  }, [columns, sort, rules, allTags]);

  const activeFiltersCount = rules.filter(r => r.value).length;

  // ─── Row selection (tg-crm pattern) ──────────────────────
  // Uses onSelect/onSelectAll instead of onChange to preserve selection across pages.
  // preserveSelectedRowKeys: true prevents Ant Design from dropping IDs not in current dataSource.
  const rowSelection = {
    selectedRowKeys,
    preserveSelectedRowKeys: true,
    columnWidth: 40,
    onSelect: (record, selected) => {
      setSelectedRowKeys(prev => {
        if (selected) {
          return prev.includes(record.id) ? prev : [...prev, record.id];
        }
        return prev.filter(k => k !== record.id);
      });
    },
    onSelectAll: (selected, _selectedRows, changeRows) => {
      const changedIds = changeRows.map(r => r.id);
      setSelectedRowKeys(prev => {
        if (selected) {
          const prevSet = new Set(prev);
          changedIds.forEach(id => prevSet.add(id));
          return Array.from(prevSet);
        }
        return prev.filter(k => !changedIds.includes(k));
      });
    },
  };

  return (
    <div>
      <Title level={3}>👥 Пользователи</Title>

      {/* ─── Bulk Actions Panel ─────────────────────────────── */}
      {selectedRowKeys.length > 0 && (
        <Card size="small" style={{ marginBottom: 12, background: '#e6f7ff', borderColor: '#91d5ff' }}>
          <Row gutter={[12, 8]} align="middle" wrap>
            <Col>
              <Tag color="blue" style={{ fontSize: 13, padding: '2px 10px' }}>
                Выбрано: {selectedRowKeys.length}
              </Tag>
            </Col>
            <Col>
              <Select
                placeholder="Выберите действие"
                value={bulkAction ?? undefined}
                options={BULK_ACTION_OPTIONS.map(o => ({ value: o.value, label: o.label }))}
                style={{ minWidth: 220 }}
                onChange={(v) => setBulkAction(v ?? null)}
                allowClear
                disabled={isBulkRunning}
              />
            </Col>
            <Col>
              <Button
                type="primary"
                onClick={handleBulkAction}
                disabled={!bulkAction || !selectedRowKeys.length}
                loading={isBulkRunning}
              >
                Выполнить
              </Button>
            </Col>
            <Col>
              <Button
                onClick={() => { setSelectedRowKeys([]); setBulkAction(null); }}
                disabled={isBulkRunning}
              >
                Очистить выбор
              </Button>
            </Col>
            {bulkProgress && (
              <Col flex="auto">
                <Progress
                  percent={Math.round((bulkProgress.processed / bulkProgress.total) * 100)}
                  size="small"
                  format={() => `${bulkProgress.processed} / ${bulkProgress.total}`}
                />
              </Col>
            )}
          </Row>
        </Card>
      )}

      <Card>
        <Row gutter={[8, 8]} style={{ marginBottom: 16 }} align="middle" wrap>
          <Col><ColumnPicker columns={columns} onChange={setColumns} /></Col>
          <Col>
            <Select
              value={limit}
              onChange={setLimit}
              style={{ width: 80 }}
              options={[
                { label: '20', value: '20' },
                { label: '50', value: '50' },
                { label: '100', value: '100' },
              ]}
            />
          </Col>
          <Col><Button icon={<SyncOutlined />} onClick={fetchUsers}>Обновить</Button></Col>
          <Col>
            <Button icon={<PlusOutlined />} onClick={() => { tagForm.resetFields(); tagForm.setFieldsValue({ color: '#2db7f5' }); setCreateTagModalVisible(true); }}>
              Создать тег
            </Button>
          </Col>
          {activeFiltersCount > 0 && (
            <Col>
              <Button 
                icon={<ClearOutlined />} 
                onClick={() => useUsersFiltersStore.getState().resetFilters()}
              >
                Сбросить фильтры ({activeFiltersCount})
              </Button>
            </Col>
          )}
        </Row>

        {/* Tag filter bar */}
        {allTags.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <Space wrap size={4} align="center">
              <Text type="secondary" style={{ fontSize: 12 }}>Фильтр по тегам:</Text>
              {allTags.map(t => {
                const isActive = filterTagIds.includes(t.id);
                return (
                  <Tag
                    key={t.id}
                    color={isActive ? t.color : 'default'}
                    style={{ cursor: 'pointer', fontSize: 12 }}
                    onClick={() => {
                      setFilterTagIds(prev =>
                        isActive ? prev.filter(id => id !== t.id) : [...prev, t.id]
                      );
                    }}
                  >
                    {isActive ? '✓ ' : ''}{t.name} ({t.users_count})
                  </Tag>
                );
              })}
              {filterTagIds.length > 0 && (
                <Button size="small" type="link" onClick={() => setFilterTagIds([])}>Сбросить</Button>
              )}
            </Space>
          </div>
        )}

        <Table
          dataSource={users}
          columns={tableColumns}
          rowKey="id"
          loading={loading}
          pagination={false}
          rowSelection={rowSelection}
          onRow={(record) => ({
            onClick: (e) => {
              if (shouldSkipRowAction(e)) return;
              setDrawerUserId(record.id);
            },
            style: { cursor: 'pointer' },
          })}
          scroll={{ x: 'max-content' }}
          size="small"
        />

        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <Pagination
            current={page}
            total={total}
            pageSize={parseInt(limit)}
            onChange={setPage}
            showSizeChanger={false}
            showTotal={(total) => `Всего ${total} записей`}
          />
        </div>
      </Card>

      <UserDetailDrawer
        userId={drawerUserId}
        apiKey={apiKey}
        visible={!!drawerUserId}
        onClose={() => setDrawerUserId(null)}
        allTags={allTags}
        communityId={communityId}
        onTagsChanged={() => { fetchUsers(); fetchTags(); }}
      />

      {/* Assign Tags Modal */}
      <AssignTagsModal
        visible={assignModalVisible}
        allTags={allTags}
        onCancel={() => { setAssignModalVisible(false); setAssignUserId(null); }}
        onAssign={(tagIds) => {
          const ids = assignUserId ? [assignUserId] : selectedRowKeys;
          handleAssignTags(tagIds, ids);
        }}
        targetCount={assignUserId ? 1 : selectedRowKeys.length}
        apiKey={apiKey}
        communityId={communityId}
        onTagCreated={fetchTags}
      />

      {/* Create Tag Modal */}
      <Modal
        title="Создать тег"
        open={createTagModalVisible}
        onCancel={() => { setCreateTagModalVisible(false); tagForm.resetFields(); }}
        onOk={() => tagForm.submit()}
        okText="Создать"
        cancelText="Отмена"
      >
        <Form form={tagForm} layout="vertical" onFinish={handleCreateTag}>
          <Form.Item name="name" label="Название" rules={[{ required: true, message: 'Введите название' }]}>
            <Input maxLength={100} placeholder="VIP, Спикер, Новичок..." />
          </Form.Item>
          <Form.Item name="color" label="Цвет">
            <ColorPicker format="hex" />
          </Form.Item>
          <Form.Item name="description" label="Описание">
            <Input.TextArea maxLength={500} rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Send Message Modal */}
      <Modal
        title={`Отправить сообщение (${selectedRowKeys.length} пользователей)`}
        open={sendMsgModalVisible}
        onCancel={() => setSendMsgModalVisible(false)}
        onOk={handleSendDM}
        okText="Отправить"
        cancelText="Отмена"
        okButtonProps={{ disabled: !sendMsgText.trim() }}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Сообщение будет отправлено от имени бота сообщества каждому выбранному пользователю в личные сообщения."
          description={
            <>
              <span>Бот сможет написать только тем, кто <b>ранее писал боту</b> (столбец «Бот может писать» = Да). </span>
              <span>Поддерживается HTML-разметка: &lt;b&gt;жирный&lt;/b&gt;, &lt;i&gt;курсив&lt;/i&gt;, &lt;a href="..."&gt;ссылка&lt;/a&gt;.</span>
            </>
          }
        />
        <Input.TextArea
          rows={6}
          maxLength={4096}
          value={sendMsgText}
          onChange={e => setSendMsgText(e.target.value)}
          placeholder="Введите текст сообщения..."
          showCount
        />
      </Modal>
    </div>
  );
}

// Assign Tags Modal component
function AssignTagsModal({ visible, allTags, onCancel, onAssign, targetCount, apiKey, communityId, onTagCreated }) {
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [newTagName, setNewTagName] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (visible) setSelectedTagIds([]);
  }, [visible]);

  const handleQuickCreate = async () => {
    if (!newTagName.trim()) return;
    setCreating(true);
    try {
      const body = { name: newTagName.trim(), color: '#2db7f5' };
      if (communityId) body.community_id = communityId;
      const res = await fetch(`${API_BASE}/api/tags`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${apiKey}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const tag = await res.json();
        message.success(`Тег "${tag.name}" создан`);
        setSelectedTagIds(prev => [...prev, tag.id]);
        setNewTagName('');
        if (onTagCreated) onTagCreated();
      } else {
        const err = await res.json();
        message.error(err.detail || 'Ошибка');
      }
    } catch (e) {
      message.error('Ошибка сети');
    }
    setCreating(false);
  };

  return (
    <Modal
      title={`Назначить теги (${targetCount} пользователей)`}
      open={visible}
      onCancel={onCancel}
      onOk={() => onAssign(selectedTagIds)}
      okText="Назначить"
      cancelText="Отмена"
      okButtonProps={{ disabled: selectedTagIds.length === 0 }}
    >
      <div style={{ marginBottom: 16 }}>
        <Text strong>Выберите теги:</Text>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
          {allTags.map(tag => {
            const selected = selectedTagIds.includes(tag.id);
            return (
              <Tag
                key={tag.id}
                color={selected ? tag.color : 'default'}
                style={{ cursor: 'pointer', borderColor: selected ? tag.color : undefined, fontSize: 13, padding: '2px 10px' }}
                onClick={() => {
                  setSelectedTagIds(prev =>
                    selected ? prev.filter(id => id !== tag.id) : [...prev, tag.id]
                  );
                }}
              >
                {selected ? '✓ ' : ''}{tag.name}
              </Tag>
            );
          })}
        </div>
      </div>

      <Divider style={{ margin: '12px 0' }} />
      <Text strong>Или создайте новый тег:</Text>
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <Input
          placeholder="Название нового тега"
          value={newTagName}
          onChange={e => setNewTagName(e.target.value)}
          onPressEnter={handleQuickCreate}
          style={{ flex: 1 }}
        />
        <Button type="primary" icon={<PlusOutlined />} onClick={handleQuickCreate} loading={creating}>
          Создать
        </Button>
      </div>
    </Modal>
  );
}

// Matches page
function MatchesPage({ apiKey, communityId }) {
  const [cycles, setCycles] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchCycles = async () => {
    setLoading(true);
    try {
      const params = communityId ? `?community_id=${communityId}` : '';
      const response = await fetch(`${API_BASE}/api/matches/cycles${params}`, {
        headers: { 'Authorization': `Bearer ${apiKey}` }
      });
      if (response.ok) {
        setCycles(await response.json());
      }
    } catch (e) {
      setCycles([]);
    }
    setLoading(false);
  };

  useEffect(() => { fetchCycles(); }, [communityId]);

  const columns = [
    { title: 'ID цикла', dataIndex: 'id', width: 80 },
    {
      title: 'Статус', dataIndex: 'status', render: (s) => {
        const colors = { pending: 'default', running: 'processing', completed: 'success', failed: 'error' };
        return <Tag color={colors[s] || 'default'}>{s}</Tag>;
      }
    },
    { title: 'Матчей', dataIndex: 'total_matches' },
    { title: 'Участников', dataIndex: 'total_matched_users' },
    { title: 'Запущен', dataIndex: 'started_at', render: (d) => d ? new Date(d).toLocaleString('ru-RU') : '-' },
    { title: 'Завершён', dataIndex: 'completed_at', render: (d) => d ? new Date(d).toLocaleString('ru-RU') : '-' },
  ];

  return (
    <div>
      <Title level={3}>☕ Матчи</Title>
      <Card>
        <Table
          dataSource={cycles}
          columns={columns}
          rowKey="id"
          loading={loading}
        />
      </Card>
    </div>
  );
}

// Karma page
function KarmaPage({ apiKey, communityId }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const params = communityId ? `?community_id=${communityId}` : '';
      const response = await fetch(`${API_BASE}/api/karma/events${params}`, {
        headers: { 'Authorization': `Bearer ${apiKey}` }
      });
      if (response.ok) {
        setEvents(await response.json());
      }
    } catch (e) {
      setEvents([]);
    }
    setLoading(false);
  };

  useEffect(() => { fetchEvents(); }, [communityId]);

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: 'От кого', dataIndex: 'sender_id' },
    { title: 'Кому', dataIndex: 'receiver_id' },
    { title: 'Очки', dataIndex: 'points', render: (v) => <Tag color="green">+{v}</Tag> },
    { title: 'Причина', dataIndex: 'reason', ellipsis: true },
    { title: 'Дата', dataIndex: 'created_at', render: (d) => new Date(d).toLocaleString('ru-RU') },
  ];

  return (
    <div>
      <Title level={3}>⭐ Карма</Title>
      <Card>
        <Table
          dataSource={events}
          columns={columns}
          rowKey="id"
          loading={loading}
        />
      </Card>
    </div>
  );
}

// Moderation page
function ModerationPage({ apiKey, communityId }) {
  const [strikes, setStrikes] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchStrikes = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/moderation/strikes`, {
        headers: { 'Authorization': `Bearer ${apiKey}` }
      });
      if (response.ok) {
        setStrikes(await response.json());
      }
    } catch (e) {
      setStrikes([]);
    }
    setLoading(false);
  };

  useEffect(() => { fetchStrikes(); }, []);

  const handleConfirm = async (strikeId, confirm) => {
    try {
      await fetch(`${API_BASE}/api/moderation/confirm-strike`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${apiKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ strike_id: strikeId, confirm })
      });
      message.success(confirm ? 'Страйк подтверждён' : 'Страйк отклонён');
      fetchStrikes();
    } catch (e) {
      message.error('Ошибка');
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: 'Пользователь', dataIndex: 'user_id' },
    { title: 'Тип', dataIndex: 'strike_type', render: (t) => <Tag color="orange">{t}</Tag> },
    { title: 'Причина', dataIndex: 'reason', ellipsis: true },
    { title: 'Активен', dataIndex: 'is_active', render: (v) => v ? <Tag color="red">Да</Tag> : <Tag>Нет</Tag> },
    { title: 'Дата', dataIndex: 'created_at', render: (d) => new Date(d).toLocaleString('ru-RU') },
    {
      title: 'Действия', render: (_, record) => !record.is_active && (
        <Space>
          <Popconfirm title="Подтвердить страйк?" onConfirm={() => handleConfirm(record.id, true)}>
            <Button size="small" type="primary" danger>Подтвердить</Button>
          </Popconfirm>
          <Popconfirm title="Отклонить страйк?" onConfirm={() => handleConfirm(record.id, false)}>
            <Button size="small">Отклонить</Button>
          </Popconfirm>
        </Space>
      )
    },
  ];

  return (
    <div>
      <Title level={3}>⚠️ Модерация</Title>
      <Card>
        <Table
          dataSource={strikes}
          columns={columns}
          rowKey="id"
          loading={loading}
        />
      </Card>
    </div>
  );
}

// Template category labels for grouping
const TEMPLATE_CATEGORIES = {
  'access_denied': 'Приветствие и доступ',
  'welcome_new': 'Приветствие и доступ',
  'welcome_returning': 'Приветствие и доступ',
  'about_coffee': 'Приветствие и доступ',
  'main_menu': 'Главное меню',
  'main_menu_no_profile': 'Главное меню',
  'help': 'Главное меню',
  'profile_start': 'Профиль',
  'profile_name_short': 'Профиль',
  'profile_name_long': 'Профиль',
  'profile_name_ok': 'Профиль',
  'profile_ask_company': 'Профиль',
  'profile_company_long': 'Профиль',
  'profile_ask_position': 'Профиль',
  'profile_position_long': 'Профиль',
  'profile_ask_employer': 'Профиль',
  'profile_confirm': 'Профиль',
  'profile_saved': 'Профиль',
  'profile_edit_restart': 'Профиль',
  'profile_view': 'Профиль',
  'profile_not_filled': 'Профиль',
  'opt_in_yes': 'Участие в Random Coffee',
  'opt_in_no': 'Участие в Random Coffee',
  'coffee_no_profile': 'Участие в Random Coffee',
  'coffee_pause_prompt': 'Участие в Random Coffee',
  'coffee_pause_set': 'Участие в Random Coffee',
  'coffee_pause_cancel': 'Участие в Random Coffee',
  'coffee_resume': 'Участие в Random Coffee',
  'coffee_already_active': 'Участие в Random Coffee',
  'coffee_enabled': 'Участие в Random Coffee',
  'coffee_disabled': 'Участие в Random Coffee',
  'match_intro': 'Матчи и фидбек',
  'feedback_request': 'Матчи и фидбек',
  'feedback_met': 'Матчи и фидбек',
  'feedback_not_met': 'Матчи и фидбек',
  'feedback_rescheduled': 'Матчи и фидбек',
  'feedback_no_contact': 'Матчи и фидбек',
  'thanks_received': 'Карма',
  'thanks_self': 'Карма',
  'thanks_low_karma': 'Карма',
  'thanks_usage': 'Карма',
  'thanks_not_found': 'Карма',
  'strike_warning': 'Модерация',
  'ghost_warning': 'Модерация',
  'action_cancelled': 'Общее',
  'no_action_cancel': 'Общее',
  'fallback': 'Общее',
};

function TemplatesEditor({ apiKey }) {
  const [meta, setMeta] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [edited, setEdited] = useState({});
  const [activeCategory, setActiveCategory] = useState(null);

  const fetchMeta = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/templates/meta`, {
        headers: { 'Authorization': `Bearer ${apiKey}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMeta(data);
        const initial = {};
        data.forEach(t => { if (t.value) initial[t.key] = t.value; });
        setEdited(initial);
      } else {
        message.error('Не удалось загрузить шаблоны');
      }
    } catch (e) {
      message.error('Не удалось подключиться к API');
    }
    setLoading(false);
  };

  useEffect(() => { fetchMeta(); }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const templates = {};
      Object.entries(edited).forEach(([k, v]) => {
        if (v && v.trim()) templates[k] = v.trim();
      });
      const response = await fetch(`${API_BASE}/api/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${apiKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ templates })
      });
      if (response.ok) {
        message.success('Шаблоны сохранены');
        fetchMeta();
      } else {
        const errData = await response.json().catch(() => null);
        message.error(errData?.detail || 'Ошибка сохранения');
      }
    } catch (e) {
      message.error('Не удалось подключиться к API');
    }
    setSaving(false);
  };

  const handleReset = (key) => {
    setEdited(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  if (loading) return <Card loading={true} />;

  const categories = {};
  meta.forEach(t => {
    const cat = TEMPLATE_CATEGORIES[t.key] || 'Другое';
    if (!categories[cat]) categories[cat] = [];
    categories[cat].push(t);
  });

  const categoryNames = Object.keys(categories);
  const currentCat = activeCategory || categoryNames[0];

  return (
    <div>
      <Alert
        type="info"
        message="Редактирование сообщений бота"
        description={
          <span>
            Здесь вы можете изменить любое сообщение, которое бот отправляет пользователям.
            Переменные вида <code>{'{name}'}</code> будут заменены реальными данными.
            Оставьте поле пустым, чтобы использовать значение по умолчанию.
            Поддерживается HTML-форматирование: <code>&lt;b&gt;</code>, <code>&lt;i&gt;</code>, <code>&lt;code&gt;</code>.
          </span>
        }
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Tabs activeKey={currentCat} onChange={setActiveCategory}>
        {categoryNames.map(cat => (
          <TabPane tab={cat} key={cat}>
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              {categories[cat].map(t => {
                const isEdited = edited[t.key] !== undefined && edited[t.key] !== '';
                const rowCount = Math.max(2, Math.min(8, (t.default || '').split('\n').length));
                return (
                  <Card
                    key={t.key}
                    size="small"
                    title={
                      <span>
                        <code style={{ fontSize: 11, color: '#888' }}>{t.key}</code>
                        {' — '}{t.description}
                        {isEdited && <Tag color="blue" style={{ marginLeft: 8 }}>Изменено</Tag>}
                      </span>
                    }
                    extra={
                      isEdited && (
                        <Button size="small" onClick={() => handleReset(t.key)}>
                          Сбросить
                        </Button>
                      )
                    }
                  >
                    <TextArea
                      rows={rowCount}
                      value={edited[t.key] !== undefined ? edited[t.key] : ''}
                      placeholder={t.default}
                      onChange={e => setEdited(prev => ({ ...prev, [t.key]: e.target.value }))}
                    />
                  </Card>
                );
              })}
            </Space>
          </TabPane>
        ))}
      </Tabs>

      <div style={{ marginTop: 16, textAlign: 'right' }}>
        <Button type="primary" onClick={handleSave} loading={saving} size="large">
          💾 Сохранить все шаблоны
        </Button>
      </div>
    </div>
  );
}


// Settings page
function SettingsPage({ apiKey }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/settings`, {
        headers: { 'Authorization': `Bearer ${apiKey}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
        form.setFieldsValue(data);
      } else {
        message.error(`Ошибка загрузки настроек (${response.status})`);
      }
    } catch (e) {
      message.error('Не удалось подключиться к API');
    }
    setLoading(false);
  };

  useEffect(() => { fetchSettings(); }, []);

  const handleSave = async (values) => {
    setSaving(true);
    try {
      const response = await fetch(`${API_BASE}/api/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${apiKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(values)
      });
      if (response.ok) {
        message.success('Настройки сохранены');
        const data = await response.json();
        setSettings(data.settings || values);
      } else {
        const errData = await response.json().catch(() => null);
        message.error(errData?.detail || `Ошибка сохранения настроек (${response.status})`);
      }
    } catch (e) {
      message.error('Не удалось подключиться к API');
    }
    setSaving(false);
  };

  const handleRunMatching = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/actions/run-matching`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${apiKey}` }
      });
      if (response.ok) {
        message.success('Матчинг запущен');
      } else {
        message.error('Ошибка запуска матчинга');
      }
    } catch (e) {
      message.error('Не удалось подключиться к API');
    }
  };

  if (loading) return <Card loading={true} />;

  return (
    <div>
      <Title level={3}>⚙️ Настройки системы</Title>
      
      <Tabs defaultActiveKey="coffee">
        <TabPane tab="☕ Random Coffee" key="coffee">
          <Card>
            <Form form={form} layout="vertical" onFinish={handleSave}>
              <Title level={5}>Расписание матчинга</Title>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item label="День недели" name="match_day_of_week">
                    <Select>
                      <Select.Option value={0}>Понедельник</Select.Option>
                      <Select.Option value={1}>Вторник</Select.Option>
                      <Select.Option value={2}>Среда</Select.Option>
                      <Select.Option value={3}>Четверг</Select.Option>
                      <Select.Option value={4}>Пятница</Select.Option>
                      <Select.Option value={5}>Суббота</Select.Option>
                      <Select.Option value={6}>Воскресенье</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Час запуска" name="match_hour">
                    <InputNumber min={0} max={23} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Часовой пояс" name="match_timezone">
                    <Select>
                      <Select.Option value="Europe/Moscow">Москва (UTC+3)</Select.Option>
                      <Select.Option value="Europe/Kaliningrad">Калининград (UTC+2)</Select.Option>
                      <Select.Option value="Asia/Yekaterinburg">Екатеринбург (UTC+5)</Select.Option>
                      <Select.Option value="Asia/Novosibirsk">Новосибирск (UTC+7)</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Divider />

              <Title level={5}>Параметры матчинга</Title>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item label="Дней до запроса фидбека" name="feedback_delay_days">
                    <InputNumber min={1} max={14} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Порог Ghost (неответов)" name="ghost_threshold">
                    <InputNumber min={1} max={10} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Окно анти-повтора (недель)" name="anti_repeat_window_weeks">
                    <InputNumber min={4} max={52} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>

              <Divider />

              <Title level={5}>Функции</Title>
              <Row gutter={16}>
                <Col span={6}>
                  <Form.Item label="Random Coffee" name="feature_random_coffee" valuePropName="checked">
                    <Switch />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item>
                <Space>
                  <Button type="primary" htmlType="submit" loading={saving}>
                    💾 Сохранить настройки
                  </Button>
                  <Popconfirm title="Запустить матчинг вручную?" onConfirm={handleRunMatching}>
                    <Button icon={<SyncOutlined />}>Запустить матчинг</Button>
                  </Popconfirm>
                </Space>
              </Form.Item>
            </Form>
          </Card>
        </TabPane>

        <TabPane tab="⭐ Карма" key="karma">
          <Card>
            <Form form={form} layout="vertical" onFinish={handleSave}>
              <Title level={5}>Начисление кармы</Title>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item label="Очков за благодарность" name="thanks_points">
                    <InputNumber min={1} max={10} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Лимит в день" name="daily_thanks_limit">
                    <InputNumber min={1} max={50} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Лимит одному юзеру в неделю" name="weekly_same_user_thanks_limit">
                    <InputNumber min={1} max={10} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>

              <Divider />

              <Title level={5}>Функции</Title>
              <Row gutter={16}>
                <Col span={6}>
                  <Form.Item label="Система кармы" name="feature_rating_system" valuePropName="checked">
                    <Switch />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="Благодарности реакциями" name="feature_reaction_thanks" valuePropName="checked">
                    <Switch />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item>
                <Button type="primary" htmlType="submit" loading={saving}>💾 Сохранить</Button>
              </Form.Item>
            </Form>
          </Card>
        </TabPane>

        <TabPane tab="⚠️ Модерация" key="moderation">
          <Card>
            <Form form={form} layout="vertical" onFinish={handleSave}>
              <Title level={5}>Лестница санкций</Title>
              <Row gutter={16}>
                <Col span={6}>
                  <Form.Item label="Предупреждение (страйков)" name="strike_warning_threshold">
                    <InputNumber min={0} max={10} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="Мьют (страйков)" name="strike_mute_threshold">
                    <InputNumber min={1} max={10} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="Бан (страйков)" name="strike_ban_threshold">
                    <InputNumber min={1} max={20} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="Длительность мьюта (часов)" name="mute_duration_hours">
                    <InputNumber min={1} max={168} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>

              <Divider />

              <Title level={5}>Автоматизация</Title>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item label="Авто-детекция токсичности" name="auto_toxic_detection" valuePropName="checked">
                    <Switch />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Модерация" name="feature_moderation" valuePropName="checked">
                    <Switch />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item>
                <Button type="primary" htmlType="submit" loading={saving}>💾 Сохранить</Button>
              </Form.Item>
            </Form>
          </Card>
        </TabPane>

        <TabPane tab="📝 Шаблоны сообщений" key="templates">
          <TemplatesEditor apiKey={apiKey} />
        </TabPane>

        <TabPane tab="🔧 Технические" key="tech">
          <Card>
            <Form form={form} layout="vertical" onFinish={handleSave}>
              <Title level={5}>Rate Limits</Title>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item label="Сообщений в секунду" name="bulk_message_rate_limit">
                    <InputNumber min={1} max={30} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
              <Alert
                type="info"
                message="Telegram ограничивает рассылку ~30 сообщений/сек. Не превышайте это значение."
                showIcon
              />
              <Divider />
              <Form.Item>
                <Button type="primary" htmlType="submit" loading={saving}>💾 Сохранить</Button>
              </Form.Item>
            </Form>
          </Card>
        </TabPane>
      </Tabs>
    </div>
  );
}

// Broadcast page
function BroadcastPage({ apiKey, communityId }) {
  const [form] = Form.useForm();
  const [sending, setSending] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);

  const handleSend = async (values) => {
    setSending(true);
    try {
      const response = await fetch(`${API_BASE}/api/actions/send-announcement`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${apiKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(values)
      });
      if (response.ok) {
        message.success('Рассылка добавлена в очередь');
        form.resetFields();
      } else {
        const errData = await response.json().catch(() => null);
        message.error(errData?.detail || `Ошибка отправки (${response.status})`);
      }
    } catch (e) {
      message.error('Не удалось подключиться к API');
    }
    setSending(false);
  };

  return (
    <div>
      <Title level={3}>📧 Рассылка сообщений</Title>
      
      <Row gutter={24}>
        <Col span={14}>
          <Card title="Новое сообщение">
            <Form form={form} layout="vertical" onFinish={handleSend}>
              <Form.Item 
                label="Целевая аудитория" 
                name="target"
                rules={[{ required: true }]}
              >
                <Select size="large">
                  <Select.Option value="all">
                    <Tooltip title="Все пользователи, запускавшие бота">
                      <span>Все пользователи <QuestionCircleOutlined style={{ color: '#999' }} /></span>
                    </Tooltip>
                  </Select.Option>
                  <Select.Option value="opted_in">
                    <Tooltip title="Пользователи, согласившиеся на участие в кофе">
                      <span>Участники кофе <QuestionCircleOutlined style={{ color: '#999' }} /></span>
                    </Tooltip>
                  </Select.Option>
                  <Select.Option value="verified">
                    <Tooltip title="Верифицированные участники чата">
                      <span>Верифицированные <QuestionCircleOutlined style={{ color: '#999' }} /></span>
                    </Tooltip>
                  </Select.Option>
                  <Select.Option value="active">
                    <Tooltip title="Активные пользователи (не на паузе, не ghost)">
                      <span>Активные <QuestionCircleOutlined style={{ color: '#999' }} /></span>
                    </Tooltip>
                  </Select.Option>
                </Select>
              </Form.Item>
              
              <Form.Item 
                label="Текст сообщения" 
                name="message"
                rules={[{ required: true, message: 'Введите текст сообщения' }]}
              >
                <TextArea 
                  rows={6} 
                  placeholder="Введите текст рассылки..."
                  showCount
                  maxLength={4000}
                />
              </Form.Item>

              <Form.Item>
                <Space>
                  <Button type="primary" htmlType="submit" loading={sending} icon={<SendOutlined />}>
                    Отправить рассылку
                  </Button>
                  <Button onClick={() => setPreviewVisible(true)}>
                    Предпросмотр
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        <Col span={10}>
          <Card title="ℹ️ Информация">
            <Alert
              type="info"
              message="Ограничения Telegram"
              description={
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  <li>Рассылка идёт с ограничением ~30 сообщений/сек</li>
                  <li>Бот может писать только тем, кто запускал /start</li>
                  <li>Заблокировавшие бот пользователи будут пропущены</li>
                </ul>
              }
              showIcon
              style={{ marginBottom: 16 }}
            />

            <Descriptions column={1} size="small" title="Статистика аудитории">
              <Descriptions.Item label="Всего пользователей">—</Descriptions.Item>
              <Descriptions.Item label="Участников кофе">—</Descriptions.Item>
              <Descriptions.Item label="Активных">—</Descriptions.Item>
            </Descriptions>
          </Card>

          <Card title="📝 Шаблоны" style={{ marginTop: 16 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Button block onClick={() => form.setFieldsValue({ message: '☕ Напоминаем о встрече Random Coffee!\n\nНе забудьте связаться с вашим партнёром по матчу.' })}>
                Напоминание о кофе
              </Button>
              <Button block onClick={() => form.setFieldsValue({ message: '🎉 Завтра новый раунд Random Coffee!\n\nПроверьте свои настройки участия.' })}>
                Анонс нового раунда
              </Button>
              <Button block onClick={() => form.setFieldsValue({ message: '📢 Важное объявление!\n\n' })}>
                Важное объявление
              </Button>
            </Space>
          </Card>
        </Col>
      </Row>

      <Modal
        title="Предпросмотр"
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        footer={null}
      >
        <Card style={{ background: '#f5f5f5' }}>
          <Text style={{ whiteSpace: 'pre-wrap' }}>
            {form.getFieldValue('message') || 'Текст сообщения...'}
          </Text>
        </Card>
      </Modal>
    </div>
  );
}

// Logs page
function LogsPage({ apiKey, communityId }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (communityId) params.set('community_id', communityId);
      const response = await fetch(`${API_BASE}/api/audit?${params}`, {
        headers: { 'Authorization': `Bearer ${apiKey}` }
      });
      if (response.ok) {
        setLogs(await response.json());
      }
    } catch (e) {
      message.error('Не удалось загрузить логи');
    }
    setLoading(false);
  };

  useEffect(() => { fetchLogs(); }, [communityId]);

  const filteredLogs = filter === 'all' ? logs : logs.filter(l => l.event_type === filter);

  const columns = [
    { 
      title: 'Время', 
      dataIndex: 'created_at', 
      width: 180,
      render: (d) => new Date(d).toLocaleString('ru-RU')
    },
    { 
      title: 'Тип события', 
      dataIndex: 'event_type', 
      width: 180,
      render: (t) => {
        const colors = {
          'USER_JOINED': 'blue',
          'USER_LEFT': 'default',
          'MATCH_CREATED': 'green',
          'MATCH_COMPLETED': 'success',
          'KARMA_GIVEN': 'gold',
          'STRIKE_ISSUED': 'red',
          'ADMIN_ACTION': 'purple',
        };
        return <Tag color={colors[t] || 'default'}>{t}</Tag>;
      }
    },
    { title: 'Описание', dataIndex: 'description', ellipsis: true },
    { 
      title: 'Пользователь', 
      dataIndex: 'user_id',
      width: 100,
      render: (id) => id || '-'
    },
  ];

  const eventTypes = [...new Set(logs.map(l => l.event_type))];

  return (
    <div>
      <Title level={3}>📋 Логи и аудит</Title>
      
      <Card>
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={8}>
            <Select 
              style={{ width: '100%' }} 
              value={filter} 
              onChange={setFilter}
              placeholder="Фильтр по типу"
            >
              <Select.Option value="all">Все события</Select.Option>
              {eventTypes.map(t => (
                <Select.Option key={t} value={t}>{t}</Select.Option>
              ))}
            </Select>
          </Col>
          <Col span={8}>
            <Button icon={<SyncOutlined />} onClick={fetchLogs}>Обновить</Button>
          </Col>
          <Col span={8} style={{ textAlign: 'right' }}>
            <Tooltip title="Экспорт логов в CSV">
              <Button icon={<ExportOutlined />}>Экспорт</Button>
            </Tooltip>
          </Col>
        </Row>

        <Table
          dataSource={filteredLogs}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 50 }}
        />
      </Card>

      <Row gutter={16} style={{ marginTop: 16 }}>
        <Col span={12}>
          <Card title="📊 Статистика событий" size="small">
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Всего событий">{logs.length}</Descriptions.Item>
              <Descriptions.Item label="За сегодня">
                {logs.filter(l => new Date(l.created_at) > new Date(Date.now() - 24*60*60*1000)).length}
              </Descriptions.Item>
              <Descriptions.Item label="Типов событий">{eventTypes.length}</Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
        <Col span={12}>
          <Card title="ℹ️ Типы событий" size="small">
            <Space wrap>
              {eventTypes.map(t => (
                <Tag key={t}>{t}: {logs.filter(l => l.event_type === t).length}</Tag>
              ))}
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

// Communities management page
function CommunitiesPage({ apiKey }) {
  const [communities, setCommunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [selectedCommunity, setSelectedCommunity] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [restarting, setRestarting] = useState(false);
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [joinFormFields, setJoinFormFields] = useState([]);
  const [joinFieldModalVisible, setJoinFieldModalVisible] = useState(false);
  const [editingJoinField, setEditingJoinField] = useState(null);
  const [joinFieldForm] = Form.useForm();

  // dnd-kit sensors — MUST be called unconditionally at the top of the
  // component (Rules of Hooks).  Calling them inside the conditional
  // ``selectedCommunity && detailData ? <Card>...` branch produced a
  // different number of hooks before/after a community was selected and
  // crashed the render with "Minified React error #310" → white screen.
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const fetchCommunities = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/communities`, { headers: { 'Authorization': `Bearer ${apiKey}` } });
      if (res.ok) setCommunities(await res.json());
    } catch (e) { message.error('Ошибка загрузки списка сообществ'); }
    setLoading(false);
  };

  const fetchDetail = async (id) => {
    setDetailLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/communities/${id}`, { headers: { 'Authorization': `Bearer ${apiKey}` } });
      if (res.ok) {
        const data = await res.json();
        setDetailData(data);
        const s = data.settings || {};
        setJoinFormFields(s.join_form_fields || []);
        const defaultProfileFields = {
          phone: 'optional', email: 'optional', city: 'required',
          company: 'required', position: 'required', employer_type: 'required',
          bio: 'optional', meeting_goals: 'required',
        };
        const adminIds = Array.isArray(s.admin_notify_user_ids)
          ? s.admin_notify_user_ids.map(String)
          : (s.admin_notify_user_ids ? String(s.admin_notify_user_ids).split(/[,\s]+/).filter(Boolean) : []);
        editForm.setFieldsValue({
          name: data.name, description: data.description,
          community_chat_id: data.community_chat_id || '', chat_link: data.chat_link || '',
          status: data.status,
          match_day_of_week: data.match_day_of_week, match_hour: data.match_hour,
          match_timezone: data.match_timezone || 'Europe/Moscow',
          feedback_delay_days: data.feedback_delay_days, ghost_threshold: data.ghost_threshold,
          join_via_bot_enabled: s.join_via_bot_enabled || false,
          join_require_approval: s.join_require_approval !== false,
          join_welcome_text: s.join_welcome_text || '',
          join_webapp_enabled: s.join_webapp_enabled || false,
          moderation_chat_id: s.moderation_chat_id || '',
          admin_notify_user_ids: adminIds,
          profile_phone: (s.profile_fields || defaultProfileFields).phone || 'optional',
          profile_email: (s.profile_fields || defaultProfileFields).email || 'optional',
          profile_city: (s.profile_fields || defaultProfileFields).city || 'required',
          profile_company: (s.profile_fields || defaultProfileFields).company || 'required',
          profile_position: (s.profile_fields || defaultProfileFields).position || 'required',
          profile_employer_type: (s.profile_fields || defaultProfileFields).employer_type || 'required',
          profile_bio: (s.profile_fields || defaultProfileFields).bio || 'optional',
          profile_meeting_goals: (s.profile_fields || defaultProfileFields).meeting_goals || 'required',
        });
      }
    } catch (e) { message.error('Ошибка загрузки данных сообщества'); }
    setDetailLoading(false);
  };

  useEffect(() => { fetchCommunities(); }, []);

  useEffect(() => {
    if (selectedCommunity) fetchDetail(selectedCommunity);
  }, [selectedCommunity]);

  const handleCreate = async (values) => {
    try {
      const res = await fetch(`${API_BASE}/api/communities`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${apiKey}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(values),
      });
      if (res.ok) {
        const result = await res.json();
        message.success('Сообщество создано');
        setCreateModalVisible(false);
        createForm.resetFields();
        await fetchCommunities();
        setSelectedCommunity(result.id);
      } else {
        const err = await res.json().catch(() => null);
        message.error(err?.detail || 'Ошибка создания');
      }
    } catch (e) { message.error('Ошибка сети'); }
  };

  const handleSave = async (values) => {
    if (!selectedCommunity) return;
    setSaving(true);
    try {
      const {
        join_via_bot_enabled, join_require_approval, join_welcome_text,
        join_webapp_enabled, moderation_chat_id, admin_notify_user_ids,
        profile_phone, profile_email, profile_city, profile_company,
        profile_position, profile_employer_type, profile_bio, profile_meeting_goals,
        ...rest
      } = values;
      const payload = { ...rest };
      if (!payload.bot_token || !payload.bot_token.trim()) delete payload.bot_token;

      const adminIdsClean = (Array.isArray(admin_notify_user_ids) ? admin_notify_user_ids : [])
        .map(s => String(s).trim())
        .filter(s => s && /^-?\d+$/.test(s))
        .map(s => parseInt(s, 10));

      const existingSettings = (detailData && detailData.settings) || {};
      payload.settings = {
        ...existingSettings,
        join_via_bot_enabled: !!join_via_bot_enabled,
        join_require_approval: join_require_approval !== false,
        join_welcome_text: join_welcome_text || '',
        join_form_fields: joinFormFields,
        join_webapp_enabled: !!join_webapp_enabled,
        moderation_chat_id: (moderation_chat_id || '').toString().trim(),
        admin_notify_user_ids: adminIdsClean,
        profile_fields: {
          phone: profile_phone || 'optional',
          email: profile_email || 'optional',
          city: profile_city || 'required',
          company: profile_company || 'required',
          position: profile_position || 'required',
          employer_type: profile_employer_type || 'required',
          bio: profile_bio || 'optional',
          meeting_goals: profile_meeting_goals || 'required',
        },
      };

      const res = await fetch(`${API_BASE}/api/communities/${selectedCommunity}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${apiKey}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        message.success('Сообщество обновлено');
        editForm.setFieldValue('bot_token', '');
        fetchDetail(selectedCommunity);
        fetchCommunities();
      } else {
        const err = await res.json().catch(() => null);
        message.error(err?.detail || 'Ошибка сохранения');
      }
    } catch (e) { message.error('Ошибка сети'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    try {
      await fetch(`${API_BASE}/api/communities/${id}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${apiKey}` },
      });
      message.success('Сообщество удалено');
      if (selectedCommunity === id) { setSelectedCommunity(null); setDetailData(null); }
      fetchCommunities();
    } catch (e) { message.error('Ошибка удаления'); }
  };

  const handleTestBot = async () => {
    if (!selectedCommunity) return;
    setTesting(true);
    try {
      const res = await fetch(`${API_BASE}/api/communities/${selectedCommunity}/test-bot`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${apiKey}` },
      });
      const data = await res.json();
      if (data.success) {
        message.success(`Бот работает: @${data.bot_username}`);
        fetchDetail(selectedCommunity);
        fetchCommunities();
      } else {
        message.error(data.error || 'Ошибка подключения к боту');
      }
    } catch (e) { message.error('Ошибка сети'); }
    setTesting(false);
  };

  const handleRestartBots = async () => {
    setRestarting(true);
    try {
      const res = await fetch(`${API_BASE}/api/bot/restart`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${apiKey}` },
      });
      const data = await res.json();
      if (data.success) message.success(data.message);
      else message.error(data.error || 'Ошибка перезапуска');
    } catch (e) { message.error('Ошибка сети'); }
    setRestarting(false);
  };

  const columns = [
    { title: 'Название', dataIndex: 'name', render: (v, r) => (
      <a onClick={() => setSelectedCommunity(r.id)} style={{ fontWeight: selectedCommunity === r.id ? 'bold' : 'normal' }}>{v}</a>
    )},
    { title: 'Бот', dataIndex: 'bot_username', render: v => v ? <Tag color="blue">@{v}</Tag> : <Tag color="default">—</Tag> },
    { title: 'Статус', dataIndex: 'status', render: s => (
      <Tag color={s === 'active' ? 'green' : s === 'paused' ? 'orange' : 'red'}>
        {s === 'active' ? 'Активно' : s === 'paused' ? 'Пауза' : 'Отключено'}
      </Tag>
    )},
    { title: 'Участников', dataIndex: 'member_count', width: 100 },
    { title: '', width: 40, render: (_, r) => (
      <Popconfirm title="Удалить сообщество и все связанные данные?" onConfirm={() => handleDelete(r.id)}>
        <Button size="small" danger type="text">X</Button>
      </Popconfirm>
    )},
  ];

  const dayOptions = [
    { value: 0, label: 'Понедельник' }, { value: 1, label: 'Вторник' }, { value: 2, label: 'Среда' },
    { value: 3, label: 'Четверг' }, { value: 4, label: 'Пятница' }, { value: 5, label: 'Суббота' },
    { value: 6, label: 'Воскресенье' },
  ];

  return (
    <div>
      <Row gutter={16} align="middle" style={{ marginBottom: 16 }}>
        <Col flex="auto"><Title level={3} style={{ margin: 0 }}>Сообщества</Title></Col>
        <Col>
          <Space>
            <Button type="primary" onClick={() => { createForm.resetFields(); setCreateModalVisible(true); }}>+ Новое сообщество</Button>
            <Popconfirm title="Перезапустить бот-сервис?" description="Все боты будут перезапущены. Новые сообщества подхватятся автоматически." onConfirm={handleRestartBots}>
              <Button loading={restarting} icon={<SyncOutlined />}>Перезапустить ботов</Button>
            </Popconfirm>
          </Space>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={8}>
          <Card size="small" title="Список сообществ" loading={loading}>
            <Table dataSource={communities} columns={columns} rowKey="id" pagination={false} size="small"
              onRow={(r) => ({ onClick: () => setSelectedCommunity(r.id), style: { cursor: 'pointer', background: selectedCommunity === r.id ? '#e6f4ff' : undefined } })}
            />
          </Card>
        </Col>

        <Col span={16}>
          {selectedCommunity && detailData ? (
            <Card loading={detailLoading} title={
              <Space>
                <span>{detailData.name}</span>
                {detailData.bot_username && <Tag color="blue">@{detailData.bot_username}</Tag>}
                <Tag color={detailData.status === 'active' ? 'green' : 'orange'}>{detailData.status}</Tag>
              </Space>
            } extra={<Text type="secondary">ID: {detailData.id} | Slug: {detailData.slug}</Text>}>
              <Form form={editForm} layout="vertical" onFinish={handleSave}>
                <Tabs defaultActiveKey="general">
                  <TabPane tab="Основное" key="general" forceRender>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item label="Название" name="name" rules={[{ required: true }]}><Input size="large" /></Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="Статус" name="status">
                          <Select size="large" options={[
                            { value: 'active', label: 'Активно' },
                            { value: 'paused', label: 'Пауза' },
                            { value: 'disabled', label: 'Отключено' },
                          ]} />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Form.Item label="Описание" name="description"><TextArea rows={2} /></Form.Item>

                    <Divider>Статистика</Divider>
                    <Row gutter={16}>
                      <Col span={8}><Statistic title="Участников" value={detailData.member_count || 0} /></Col>
                      <Col span={8}><Statistic title="Бот" value={detailData.bot_username ? `@${detailData.bot_username}` : 'Не настроен'} valueStyle={{ color: detailData.bot_username ? '#3f8600' : '#cf1322' }} /></Col>
                      <Col span={8}><Statistic title="Чат подключён" value={detailData.community_chat_id ? 'Да' : 'Нет'} valueStyle={{ color: detailData.community_chat_id ? '#3f8600' : '#cf1322' }} /></Col>
                    </Row>
                  </TabPane>

                  <TabPane tab="Бот и чат" key="bot" forceRender>
                    <Alert type="info" showIcon style={{ marginBottom: 16 }}
                      message="Настройка бота"
                      description={<ol style={{ margin: 0, paddingLeft: 20 }}>
                        <li>Создайте бота через <a href="https://t.me/BotFather" target="_blank" rel="noreferrer">@BotFather</a> — команда /newbot</li>
                        <li>Скопируйте токен и вставьте в поле ниже</li>
                        <li>Добавьте бота в ваш чат и сделайте его администратором</li>
                        <li>Напишите в чате команду /chatid — бот покажет Chat ID</li>
                        <li>Вставьте Chat ID в поле «ID чата» и сохраните</li>
                        <li>Нажмите «Перезапустить ботов» чтобы изменения вступили в силу</li>
                      </ol>}
                    />

                    <Row gutter={16}>
                      <Col span={24}>
                        <Form.Item label="Токен бота" name="bot_token"
                          extra={detailData.bot_token_set
                            ? 'Токен уже сохранён. Оставьте пустым, чтобы не менять, или введите новый.'
                            : 'Токен от @BotFather (123456789:ABCdef...)'}
                        >
                          <Input.Password prefix={<KeyOutlined />} size="large"
                            placeholder={detailData.bot_token_set ? 'Токен сохранён — введите новый для замены' : '123456789:ABCdef...'} />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item label="ID чата сообщества" name="community_chat_id"
                          extra="Числовой ID чата (например: -1001234567890). Узнайте через /chatid">
                          <Input prefix={<MessageOutlined />} size="large" placeholder="-1001234567890" />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="Ссылка на чат" name="chat_link" extra="Ссылка-приглашение или username чата">
                          <Input prefix={<LinkOutlined />} size="large" placeholder="https://t.me/your_chat" />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Divider />
                    <Space>
                      <Button onClick={handleTestBot} loading={testing} disabled={!detailData.bot_token_set} icon={<ApiOutlined />}>
                        Проверить бота
                      </Button>
                    </Space>
                  </TabPane>

                  <TabPane tab="Матчинг" key="matching" forceRender>
                    <Title level={5}>Расписание</Title>
                    <Row gutter={16}>
                      <Col span={8}>
                        <Form.Item label="День недели" name="match_day_of_week">
                          <Select options={dayOptions} />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item label="Час запуска" name="match_hour">
                          <InputNumber min={0} max={23} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item label="Часовой пояс" name="match_timezone">
                          <Select>
                            <Select.Option value="Europe/Moscow">Москва (UTC+3)</Select.Option>
                            <Select.Option value="Europe/Kaliningrad">Калининград (UTC+2)</Select.Option>
                            <Select.Option value="Asia/Yekaterinburg">Екатеринбург (UTC+5)</Select.Option>
                            <Select.Option value="Asia/Novosibirsk">Новосибирск (UTC+7)</Select.Option>
                            <Select.Option value="Asia/Vladivostok">Владивосток (UTC+10)</Select.Option>
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>

                    <Divider />
                    <Title level={5}>Параметры</Title>
                    <Row gutter={16}>
                      <Col span={8}>
                        <Form.Item label="Дней до запроса фидбека" name="feedback_delay_days">
                          <InputNumber min={1} max={14} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item label="Порог Ghost (неответов)" name="ghost_threshold">
                          <InputNumber min={1} max={10} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                    </Row>
                  </TabPane>

                  <TabPane tab="Вступление" key="join" forceRender>
                    <Alert
                      type="info" showIcon
                      message="Вступление через бота"
                      description="Когда включено, пользователи, которые ещё не являются участниками чата, при отправке /start боту получат форму заявки на вступление. После заполнения заявка появится на странице «Заявки» для одобрения/отклонения."
                      style={{ marginBottom: 16 }}
                    />

                    <Form.Item name="join_via_bot_enabled" valuePropName="checked">
                      <Switch checkedChildren="Включено" unCheckedChildren="Выключено" />
                    </Form.Item>

                    <Form.Item
                      label="Требовать одобрение администратора"
                      name="join_require_approval"
                      valuePropName="checked"
                      extra="Если выключено, заявка будет одобрена автоматически, а ссылка-приглашение отправлена сразу"
                    >
                      <Switch checkedChildren="Да" unCheckedChildren="Нет" />
                    </Form.Item>

                    <Form.Item
                      label="Приветственный текст"
                      name="join_welcome_text"
                      extra="Текст, который увидит пользователь перед заполнением заявки. Поддерживает HTML."
                    >
                      <Input.TextArea
                        rows={4}
                        placeholder="Добро пожаловать! Для вступления в сообщество заполните короткую заявку..."
                      />
                    </Form.Item>

                    <Divider>Уведомления модераторам в Telegram</Divider>
                    <Alert
                      type="info" showIcon style={{ marginBottom: 16 }}
                      message="Куда слать новые заявки?"
                      description={
                        <div>
                          Каждая новая заявка будет отправлена в указанный чат и/или в личку конкретным админам
                          с инлайн-кнопками <b>«Одобрить»</b> и <b>«Отклонить»</b>.<br />
                          Чтобы узнать chat_id чата модераторов: добавьте бота в чат и введите <code>/chatid</code>.<br />
                          Чтобы узнать telegram_user_id админа: попросите его написать боту <code>/start</code> и посмотреть User ID, или используйте <a href="https://t.me/userinfobot" target="_blank" rel="noreferrer">@userinfobot</a>.
                        </div>
                      }
                    />
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item
                          label="ID чата модераторов"
                          name="moderation_chat_id"
                          extra="Telegram chat_id (например: -1001234567890). Бот должен быть в этом чате."
                        >
                          <Input prefix={<MessageOutlined />} placeholder="-1001234567890" />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          label="Админы для уведомлений в личку"
                          name="admin_notify_user_ids"
                          extra="telegram_user_id админов, нажмите Enter после каждого. Каждый из них должен написать боту /start минимум раз."
                        >
                          <Select
                            mode="tags"
                            tokenSeparators={[',', ' ', ';', '\n']}
                            placeholder="123456789, 987654321"
                            style={{ width: '100%' }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Divider>Telegram Mini App (форма в WebApp-окне)</Divider>
                    <Alert
                      type="info" showIcon style={{ marginBottom: 16 }}
                      message="Альтернативная форма заявки"
                      description={
                        <div>
                          Если включено — пользователь увидит кнопку «📝 Открыть форму», открывающую
                          одностраничную WebApp-форму вместо пошагового диалога.
                          Используются те же поля, что и для пошагового диалога (см. таблицу ниже).
                        </div>
                      }
                    />
                    <Form.Item name="join_webapp_enabled" valuePropName="checked" label="Включить Mini App">
                      <Switch checkedChildren="Включено" unCheckedChildren="Выключено" />
                    </Form.Item>
                    {selectedCommunity && (
                      <Form.Item label="URL формы (для BotFather → Menu Button или ссылки клиенту)">
                        <Input
                          readOnly
                          value={`${window.location.origin}/webapp/join/${selectedCommunity}`}
                          addonAfter={
                            <a onClick={() => {
                              navigator.clipboard?.writeText(`${window.location.origin}/webapp/join/${selectedCommunity}`);
                              message.success('Скопировано');
                            }}>Копировать</a>
                          }
                        />
                      </Form.Item>
                    )}

                    <Divider>Поля анкеты на вступление</Divider>
                    <Alert
                      type="info" showIcon
                      message="Настройте поля формы заявки на вступление"
                      description="Добавьте поля, которые должен заполнить пользователь при подаче заявки. Перетаскивайте строки для изменения порядка."
                      style={{ marginBottom: 16 }}
                    />

                    <DndContext
                      sensors={dndSensors}
                      collisionDetection={closestCenter}
                      onDragEnd={(event) => {
                        const { active, over } = event;
                        if (active.id !== over?.id) {
                          setJoinFormFields(prev => {
                            const oldIndex = prev.findIndex(f => f.id === active.id);
                            const newIndex = prev.findIndex(f => f.id === over.id);
                            return arrayMove(prev, oldIndex, newIndex);
                          });
                        }
                      }}
                    >
                      <SortableContext
                        items={joinFormFields.map(f => f.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        <Table
                          size="small"
                          pagination={false}
                          dataSource={joinFormFields}
                          rowKey="id"
                          components={{ body: { row: SortableRow } }}
                          onRow={(record) => ({ id: record.id })}
                          columns={[
                            {
                              title: '', width: 40,
                              render: () => <HolderOutlined style={{ cursor: 'grab', color: '#999' }} />,
                            },
                            { title: 'Поле', dataIndex: 'label', ellipsis: true },
                            {
                              title: 'Тип', dataIndex: 'type', width: 140,
                              render: v => ({
                                text: 'Текст', email: 'Email', tel: 'Телефон',
                                select: 'Выпадающий', radio: 'Радио', textarea: 'Текстовое поле',
                                checkbox: 'Чекбокс', contact: 'ТГ-контакт',
                              }[v] || v),
                            },
                            {
                              title: 'Обяз.', dataIndex: 'required', width: 70,
                              render: v => v ? <Tag color="red">Да</Tag> : <Tag>Нет</Tag>,
                            },
                            {
                              title: '', width: 120,
                              render: (_, record, index) => (
                                <Space size="small">
                                  <Button size="small" icon={<EditOutlined />} onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingJoinField(index);
                                    const fu = record.follow_up_field || {};
                                    joinFieldForm.setFieldsValue({
                                      ...record,
                                      options_text: (record.options || []).join('\n'),
                                      followup_trigger: fu.trigger_value || '',
                                      followup_label: fu.label || '',
                                      followup_placeholder: fu.placeholder || '',
                                      followup_required: fu.required !== false,
                                      followup_type: fu.type || 'text',
                                    });
                                    setJoinFieldModalVisible(true);
                                  }} />
                                  <Popconfirm title="Удалить поле?" onConfirm={() => {
                                    setJoinFormFields(prev => prev.filter((_, i) => i !== index));
                                  }}>
                                    <Button size="small" danger icon={<DeleteOutlined />} />
                                  </Popconfirm>
                                </Space>
                              ),
                            },
                          ]}
                        />
                      </SortableContext>
                    </DndContext>

                    <Space style={{ marginTop: 12, width: '100%' }} direction="vertical">
                      <Button
                        type="dashed" block
                        icon={<PlusOutlined />}
                        onClick={() => {
                          setEditingJoinField(null);
                          joinFieldForm.resetFields();
                          joinFieldForm.setFieldsValue({ type: 'text', required: true });
                          setJoinFieldModalVisible(true);
                        }}
                      >
                        Добавить поле
                      </Button>
                      <Popconfirm
                        title="Применить шаблон E-Commerce Club?"
                        description={
                          joinFormFields.length > 0
                            ? 'Текущие поля анкеты будут ЗАМЕНЕНЫ на 10 полей со справочниками (ФИО, должность, уровень позиции, компания, email, телефон, функция, сегмент, индустрия, стек).'
                            : 'Заполнит анкету 10 полями со справочниками E-Commerce Club. Не забудьте затем нажать «Сохранить».'
                        }
                        okText="Применить"
                        cancelText="Отмена"
                        onConfirm={() => {
                          setJoinFormFields(ECOMMERCE_CLUB_JOIN_FIELDS.map(f => ({ ...f })));
                          message.success('Шаблон E-Commerce Club применён. Нажмите «Сохранить» для применения.');
                        }}
                      >
                        <Button block icon={<ApiOutlined />}>
                          Применить шаблон E-Commerce Club
                        </Button>
                      </Popconfirm>
                    </Space>
                  </TabPane>

                  <TabPane tab="Анкета Random Coffee" key="profile-fields" forceRender>
                    <Alert
                      type="info" showIcon
                      message="Настройка анкеты Random Coffee"
                      description="Настройте, какие поля будет видеть участник при заполнении профиля для Random Coffee. Имя и фамилия всегда обязательны. Это отдельная анкета от формы вступления."
                      style={{ marginBottom: 16 }}
                    />
                    <Table
                      size="small"
                      pagination={false}
                      dataSource={[
                        { key: 'phone', label: 'Телефон', field: 'profile_phone' },
                        { key: 'email', label: 'Email', field: 'profile_email' },
                        { key: 'city', label: 'Город', field: 'profile_city' },
                        { key: 'company', label: 'Компания', field: 'profile_company' },
                        { key: 'position', label: 'Должность', field: 'profile_position' },
                        { key: 'employer_type', label: 'Тип работодателя', field: 'profile_employer_type' },
                        { key: 'bio', label: 'О себе', field: 'profile_bio' },
                        { key: 'meeting_goals', label: 'Цели встреч', field: 'profile_meeting_goals' },
                      ]}
                      columns={[
                        { title: 'Поле', dataIndex: 'label', width: 200 },
                        {
                          title: 'Режим', dataIndex: 'field', width: 200,
                          render: (field) => (
                            <Form.Item name={field} noStyle>
                              <Select style={{ width: 180 }}>
                                <Select.Option value="required">Обязательное</Select.Option>
                                <Select.Option value="optional">Необязательное</Select.Option>
                                <Select.Option value="hidden">Скрыто</Select.Option>
                              </Select>
                            </Form.Item>
                          ),
                        },
                      ]}
                    />
                  </TabPane>
                </Tabs>

                <Divider />
                <Button type="primary" htmlType="submit" loading={saving} size="large">Сохранить</Button>
              </Form>
            </Card>
          ) : (
            <Card>
              <div style={{ textAlign: 'center', padding: 60, color: '#999' }}>
                <ApiOutlined style={{ fontSize: 48, marginBottom: 16 }} />
                <br />
                <Text type="secondary">Выберите сообщество из списка слева или создайте новое</Text>
              </div>
            </Card>
          )}
        </Col>
      </Row>

      <Modal
        title="Новое сообщество"
        open={createModalVisible}
        onCancel={() => setCreateModalVisible(false)}
        onOk={() => createForm.submit()}
        okText="Создать"
      >
        <Form form={createForm} layout="vertical" onFinish={handleCreate}>
          <Form.Item name="name" label="Название" rules={[{ required: true, message: 'Введите название' }]}>
            <Input placeholder="Например: AdTech Community" />
          </Form.Item>
          <Form.Item name="slug" label="Slug (латиница, уникальный)" rules={[{ required: true, message: 'Введите slug' }]}>
            <Input placeholder="adtech-community" />
          </Form.Item>
          <Form.Item name="description" label="Описание">
            <TextArea rows={2} placeholder="Краткое описание сообщества" />
          </Form.Item>
          <Divider>Опционально (можно настроить позже)</Divider>
          <Form.Item name="bot_token" label="Токен бота" extra="Можно добавить после создания">
            <Input.Password placeholder="123456789:ABCdef..." />
          </Form.Item>
          <Form.Item name="community_chat_id" label="Chat ID" extra="Можно узнать позже через /chatid">
            <Input placeholder="-1001234567890" />
          </Form.Item>
          <Form.Item name="chat_link" label="Ссылка на чат">
            <Input placeholder="https://t.me/your_chat" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editingJoinField !== null ? "Редактировать поле" : "Новое поле анкеты"}
        open={joinFieldModalVisible}
        onCancel={() => { setJoinFieldModalVisible(false); setEditingJoinField(null); }}
        onOk={() => joinFieldForm.submit()}
        okText="Сохранить"
        width={500}
      >
        <Form
          form={joinFieldForm}
          layout="vertical"
          onFinish={(values) => {
            const field = {
              id: values.id || `field_${Date.now()}`,
              label: values.label,
              type: values.type,
              required: !!values.required,
              placeholder: values.placeholder || '',
            };
            if (values.description) {
              field.description = values.description;
            }
            if (['select', 'radio'].includes(values.type) && values.options_text) {
              field.options = values.options_text.split('\n').map(s => s.trim()).filter(Boolean);
            }
            if (
              ['select', 'radio'].includes(values.type) &&
              values.followup_trigger && values.followup_label
            ) {
              field.follow_up_field = {
                id: `${field.id}_followup`,
                trigger_value: values.followup_trigger,
                label: values.followup_label,
                type: values.followup_type || 'text',
                required: values.followup_required !== false,
                placeholder: values.followup_placeholder || '',
              };
            }
            setJoinFormFields(prev => {
              if (editingJoinField !== null) {
                const copy = [...prev];
                copy[editingJoinField] = { ...copy[editingJoinField], ...field };
                return copy;
              }
              return [...prev, field];
            });
            setJoinFieldModalVisible(false);
            setEditingJoinField(null);
            joinFieldForm.resetFields();
          }}
        >
          <Form.Item name="label" label="Название поля" rules={[{ required: true, message: 'Введите название' }]}>
            <Input placeholder="Например: ФИО, Должность, Индустрия..." />
          </Form.Item>
          <Form.Item name="type" label="Тип поля" rules={[{ required: true }]}>
            <Select>
              <Select.Option value="text">Текст</Select.Option>
              <Select.Option value="email">Email</Select.Option>
              <Select.Option value="tel">Телефон</Select.Option>
              <Select.Option value="select">Выпадающий список</Select.Option>
              <Select.Option value="radio">Радио-кнопки</Select.Option>
              <Select.Option value="textarea">Текстовое поле (длинный текст)</Select.Option>
              <Select.Option value="checkbox">Чекбокс (да/нет)</Select.Option>
              <Select.Option value="contact">Запрос ТГ-контакта (телефон)</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="required" valuePropName="checked">
            <Switch checkedChildren="Обязательное" unCheckedChildren="Необязательное" />
          </Form.Item>
          <Form.Item name="placeholder" label="Подсказка (placeholder)">
            <Input placeholder="Текст-подсказка для пользователя" />
          </Form.Item>
          <Form.Item
            name="description"
            label="Описание / пояснение"
            extra="Дополнительный текст, который будет показан пользователю вместе с вопросом (например, краткая политика конфиденциальности)"
          >
            <Input.TextArea rows={3} placeholder="Текст пояснения для пользователя..." />
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.type !== cur.type}>
            {({ getFieldValue }) =>
              ['select', 'radio'].includes(getFieldValue('type')) ? (
                <>
                  <Form.Item
                    name="options_text"
                    label="Варианты ответа (каждый на новой строке)"
                    rules={[{ required: true, message: 'Введите варианты' }]}
                  >
                    <Input.TextArea rows={6} placeholder={"FMCG\nFashion\nКрасота\nЭлектроника\nIT/Telecom"} />
                  </Form.Item>
                  <Divider>Уточняющий вопрос (например, «Свой вариант»)</Divider>
                  <Alert
                    type="info" showIcon style={{ marginBottom: 12 }}
                    message="Опционально"
                    description="Если пользователь выберет указанный вариант — бот задаст дополнительный текстовый вопрос."
                  />
                  <Form.Item name="followup_trigger" label="Триггер-значение (точное название варианта)">
                    <Input placeholder="Свой вариант" />
                  </Form.Item>
                  <Form.Item name="followup_label" label="Текст уточняющего вопроса">
                    <Input placeholder="Уточните индустрию" />
                  </Form.Item>
                  <Form.Item name="followup_placeholder" label="Подсказка для уточнения">
                    <Input placeholder="Например: Складская логистика" />
                  </Form.Item>
                  <Form.Item name="followup_required" valuePropName="checked" initialValue={true}>
                    <Switch checkedChildren="Уточнение обязательно" unCheckedChildren="Уточнение необязательно" />
                  </Form.Item>
                  <Form.Item name="followup_type" hidden initialValue="text"><Input /></Form.Item>
                </>
              ) : null
            }
          </Form.Item>
          <Form.Item name="id" hidden><Input /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
}


// ─── Applications Management Page ──────────────────────────────────────

function ApplicationsPage({ apiKey, communityId }) {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [actionLoading, setActionLoading] = useState(null);
  const [rejectModalApp, setRejectModalApp] = useState(null);
  const [rejectNote, setRejectNote] = useState('');
  const [detailApp, setDetailApp] = useState(null);

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (communityId) params.set('community_id', communityId);
      if (statusFilter) params.set('status', statusFilter);
      const res = await fetch(`${API_BASE}/api/applications?${params}`, {
        headers: { 'Authorization': `Bearer ${apiKey}` },
      });
      if (res.ok) setApplications(await res.json());
    } catch (e) { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { fetchApplications(); }, [communityId, statusFilter]);

  const handleApprove = async (appId) => {
    setActionLoading(appId);
    try {
      const res = await fetch(`${API_BASE}/api/applications/${appId}/approve`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${apiKey}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (res.ok) {
        const data = await res.json();
        message.success(`Заявка одобрена! Ссылка отправлена пользователю.`);
        fetchApplications();
      } else {
        const err = await res.json().catch(() => null);
        message.error(err?.detail || 'Ошибка одобрения');
      }
    } catch (e) { message.error('Ошибка сети'); }
    setActionLoading(null);
  };

  const handleReject = async () => {
    if (!rejectModalApp) return;
    setActionLoading(rejectModalApp.id);
    try {
      const res = await fetch(`${API_BASE}/api/applications/${rejectModalApp.id}/reject`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${apiKey}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_note: rejectNote }),
      });
      if (res.ok) {
        message.success('Заявка отклонена');
        setRejectModalApp(null);
        setRejectNote('');
        fetchApplications();
      } else {
        const err = await res.json().catch(() => null);
        message.error(err?.detail || 'Ошибка');
      }
    } catch (e) { message.error('Ошибка сети'); }
    setActionLoading(null);
  };

  const statusColors = { pending: 'orange', approved: 'green', rejected: 'red' };
  const statusLabels = { pending: 'На рассмотрении', approved: 'Одобрена', rejected: 'Отклонена' };

  const columns = [
    {
      title: 'Дата', dataIndex: 'created_at', width: 150,
      render: v => v ? new Date(v).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—',
    },
    {
      title: 'Данные заявки', key: 'form_summary', ellipsis: true,
      render: (_, r) => {
        const fd = r.form_data || {};
        const entries = Object.entries(fd).slice(0, 3);
        return entries.map(([, v]) => v).filter(Boolean).join(' | ') || '—';
      },
    },
    {
      title: 'Telegram', key: 'telegram',
      render: (_, r) => r.user_username ? <a href={`https://t.me/${r.user_username}`} target="_blank" rel="noreferrer">@{r.user_username}</a> : `ID: ${r.user_telegram_id || '—'}`,
    },
    {
      title: 'Статус', dataIndex: 'status', width: 140,
      render: v => <Tag color={statusColors[v]}>{statusLabels[v] || v}</Tag>,
    },
    {
      title: 'Действия', key: 'actions', width: 200,
      render: (_, r) => r.status === 'pending' ? (
        <Space>
          <Popconfirm title="Одобрить заявку?" onConfirm={() => handleApprove(r.id)} okText="Да" cancelText="Нет">
            <Button type="primary" size="small" loading={actionLoading === r.id} icon={<CheckOutlined />}>
              Одобрить
            </Button>
          </Popconfirm>
          <Button danger size="small" onClick={() => { setRejectModalApp(r); setRejectNote(''); }} icon={<CloseOutlined />}>
            Отклонить
          </Button>
        </Space>
      ) : (
        <Button size="small" onClick={() => setDetailApp(r)}>Детали</Button>
      ),
    },
  ];

  return (
    <div>
      <Card
        title="Заявки на вступление"
        extra={
          <Space>
            <Select value={statusFilter} onChange={setStatusFilter} style={{ width: 180 }}>
              <Select.Option value="pending">На рассмотрении</Select.Option>
              <Select.Option value="approved">Одобренные</Select.Option>
              <Select.Option value="rejected">Отклонённые</Select.Option>
              <Select.Option value="">Все</Select.Option>
            </Select>
            <Button onClick={fetchApplications} icon={<SyncOutlined />}>Обновить</Button>
          </Space>
        }
      >
        {!communityId && (
          <Alert type="warning" message="Выберите сообщество в верхнем меню для просмотра заявок" showIcon style={{ marginBottom: 16 }} />
        )}
        <Table
          dataSource={applications}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 20 }}
          onRow={(r) => ({ onClick: () => setDetailApp(r), style: { cursor: 'pointer' } })}
          size="small"
        />
      </Card>

      <Modal
        title="Отклонение заявки"
        open={!!rejectModalApp}
        onCancel={() => setRejectModalApp(null)}
        onOk={handleReject}
        okText="Отклонить"
        okButtonProps={{ danger: true, loading: actionLoading === rejectModalApp?.id }}
      >
        <p>Вы уверены, что хотите отклонить заявку от <b>{rejectModalApp?.form_data?.full_name}</b>?</p>
        <Input.TextArea
          rows={3}
          value={rejectNote}
          onChange={e => setRejectNote(e.target.value)}
          placeholder="Причина отклонения (необязательно, будет отправлена пользователю)"
        />
      </Modal>

      <Drawer
        title="Детали заявки"
        open={!!detailApp}
        onClose={() => setDetailApp(null)}
        width={500}
      >
        {detailApp && (
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="ID">{detailApp.id}</Descriptions.Item>
            <Descriptions.Item label="Статус">
              <Tag color={statusColors[detailApp.status]}>{statusLabels[detailApp.status] || detailApp.status}</Tag>
            </Descriptions.Item>
            {Object.entries(detailApp.form_data || {}).map(([key, val]) => (
              <Descriptions.Item key={key} label={key}>{val || '—'}</Descriptions.Item>
            ))}
            <Descriptions.Item label="Telegram">
              {detailApp.user_username ? <a href={`https://t.me/${detailApp.user_username}`} target="_blank" rel="noreferrer">@{detailApp.user_username}</a> : `ID: ${detailApp.user_telegram_id || '—'}`}
            </Descriptions.Item>
            <Descriptions.Item label="Дата подачи">{detailApp.created_at ? new Date(detailApp.created_at).toLocaleString('ru-RU') : '—'}</Descriptions.Item>
            {detailApp.reviewed_at && (
              <Descriptions.Item label="Дата рассмотрения">{new Date(detailApp.reviewed_at).toLocaleString('ru-RU')}</Descriptions.Item>
            )}
            {detailApp.admin_note && (
              <Descriptions.Item label="Заметка админа">{detailApp.admin_note}</Descriptions.Item>
            )}
            {detailApp.invite_link && (
              <Descriptions.Item label="Ссылка-приглашение">
                <a href={detailApp.invite_link} target="_blank" rel="noreferrer">{detailApp.invite_link}</a>
              </Descriptions.Item>
            )}
          </Descriptions>
        )}

        {detailApp && detailApp.status === 'pending' && (
          <div style={{ marginTop: 24 }}>
            <Space>
              <Popconfirm title="Одобрить заявку?" onConfirm={() => { handleApprove(detailApp.id); setDetailApp(null); }} okText="Да" cancelText="Нет">
                <Button type="primary" icon={<CheckOutlined />} loading={actionLoading === detailApp.id}>Одобрить</Button>
              </Popconfirm>
              <Button danger icon={<CloseOutlined />} onClick={() => { setRejectModalApp(detailApp); setDetailApp(null); }}>Отклонить</Button>
            </Space>
          </div>
        )}
      </Drawer>
    </div>
  );
}


// Community selector hook
// ─── Tags Management Page ───────────────────────────────────────────

function TagsPage({ apiKey, communityId }) {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [form] = Form.useForm();

  const fetchTags = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (communityId) params.set('community_id', communityId);
      const res = await fetch(`${API_BASE}/api/tags?${params}`, {
        headers: { 'Authorization': `Bearer ${apiKey}` },
      });
      if (res.ok) setTags(await res.json());
    } catch (e) { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { fetchTags(); }, [communityId]);

  const handleSave = async (values) => {
    const color = typeof values.color === 'string' ? values.color
      : values.color?.toHexString ? values.color.toHexString()
      : '#2db7f5';
    const body = { ...values, color };
    if (!editingTag && communityId) body.community_id = communityId;

    try {
      const url = editingTag
        ? `${API_BASE}/api/tags/${editingTag.id}`
        : `${API_BASE}/api/tags`;
      const res = await fetch(url, {
        method: editingTag ? 'PUT' : 'POST',
        headers: { 'Authorization': `Bearer ${apiKey}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        message.success(editingTag ? 'Тег обновлён' : 'Тег создан');
        setModalVisible(false);
        form.resetFields();
        setEditingTag(null);
        fetchTags();
      } else {
        const err = await res.json();
        message.error(err.detail || 'Ошибка');
      }
    } catch (e) {
      message.error('Ошибка сети');
    }
  };

  const handleDelete = async (id) => {
    try {
      await fetch(`${API_BASE}/api/tags/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${apiKey}` },
      });
      message.success('Тег удалён');
      fetchTags();
    } catch (e) {
      message.error('Ошибка');
    }
  };

  const openEdit = (tag) => {
    setEditingTag(tag);
    form.setFieldsValue({ name: tag.name, color: tag.color, description: tag.description || '' });
    setModalVisible(true);
  };

  const openCreate = () => {
    setEditingTag(null);
    form.resetFields();
    form.setFieldsValue({ color: '#2db7f5' });
    setModalVisible(true);
  };

  const columns = [
    {
      title: 'Цвет',
      dataIndex: 'color',
      width: 60,
      render: (color) => (
        <div style={{ width: 24, height: 24, borderRadius: 4, background: color, border: '1px solid #d9d9d9' }} />
      ),
    },
    { title: 'Название', dataIndex: 'name', key: 'name' },
    { title: 'Описание', dataIndex: 'description', key: 'description', render: (d) => d || '—' },
    {
      title: 'Пользователей',
      dataIndex: 'users_count',
      key: 'users_count',
      width: 130,
      render: (v) => <Badge count={v} showZero style={{ backgroundColor: v > 0 ? '#1890ff' : '#d9d9d9' }} />,
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Tooltip title="Редактировать">
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          </Tooltip>
          <Popconfirm
            title="Удалить тег?"
            description="Тег будет снят со всех пользователей"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row gutter={16} align="middle" style={{ marginBottom: 16 }}>
        <Col flex="auto"><Title level={3} style={{ margin: 0 }}>🏷️ Теги</Title></Col>
        <Col>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Создать тег
          </Button>
        </Col>
      </Row>

      <Card>
        <Table
          dataSource={tags}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={false}
        />
      </Card>

      <Modal
        title={editingTag ? 'Редактировать тег' : 'Создать тег'}
        open={modalVisible}
        onCancel={() => { setModalVisible(false); setEditingTag(null); form.resetFields(); }}
        onOk={() => form.submit()}
        okText="Сохранить"
        cancelText="Отмена"
      >
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item name="name" label="Название" rules={[{ required: true, message: 'Введите название тега' }]}>
            <Input maxLength={100} placeholder="Например: VIP, Новичок, Спикер" />
          </Form.Item>
          <Form.Item name="color" label="Цвет">
            <ColorPicker format="hex" />
          </Form.Item>
          <Form.Item name="description" label="Описание">
            <Input.TextArea maxLength={500} rows={2} placeholder="Необязательное описание тега" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}


function useCommunitySelector(apiKey) {
  const [communities, setCommunities] = useState([]);
  const [selectedId, setSelectedId] = useState(() => {
    const stored = localStorage.getItem('selectedCommunityId');
    const parsed = stored ? Number.parseInt(stored, 10) : NaN;
    return Number.isFinite(parsed) ? parsed : null;
  });

  useEffect(() => {
    let cancelled = false;
    if (!apiKey) {
      setCommunities([]);
      return () => { cancelled = true; };
    }

    fetch(`${API_BASE}/api/communities`, { headers: { 'Authorization': `Bearer ${apiKey}` } })
      .then(async response => {
        if (!response.ok) throw new Error(`Failed to load communities: ${response.status}`);
        const data = await response.json();
        if (!Array.isArray(data)) throw new Error('Communities response is not an array');
        return data;
      })
      .then(data => {
        if (cancelled) return;
        setCommunities(data);
        setSelectedId(currentId => {
          const nextId = data.some(community => community.id === currentId)
            ? currentId
            : data[0]?.id ?? null;

          if (nextId === null) localStorage.removeItem('selectedCommunityId');
          else localStorage.setItem('selectedCommunityId', nextId);
          return nextId;
        });
      })
      .catch(() => {
        if (!cancelled) setCommunities([]);
      });

    return () => { cancelled = true; };
  }, [apiKey]);

  const select = (id) => {
    setSelectedId(id);
    localStorage.setItem('selectedCommunityId', id);
  };

  const selected = communities.find(c => c.id === selectedId);

  return { communities, selectedId, select, selected };
}


// Main app with React Router
function App() {
  const [apiKey, setApiKey] = useState(localStorage.getItem('apiKey'));
  const [authChecked, setAuthChecked] = useState(false);
  const [currentUser, setCurrentUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('user') || 'null'); }
    catch { return null; }
  });
  const [collapsed, setCollapsed] = useState(false);
  const [helpVisible, setHelpVisible] = useState(false);
  const [pendingAppCount, setPendingAppCount] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { communities, selectedId, select: selectCommunity, selected: selectedCommunity } = useCommunitySelector(apiKey);

  // Validate stored token against the API on mount; clear if invalid/expired.
  useEffect(() => {
    let cancelled = false;
    const verify = async () => {
      if (!apiKey) {
        if (!cancelled) setAuthChecked(true);
        return;
      }
      try {
        const res = await fetch(`${API_BASE}/api/auth/verify`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${apiKey}` },
        });
        if (!res.ok) throw new Error('invalid');
        const data = await res.json();
        if (!cancelled && data.valid && data.user) {
          setCurrentUser(data.user);
          localStorage.setItem('user', JSON.stringify(data.user));
        }
      } catch (_) {
        if (!cancelled) {
          localStorage.removeItem('apiKey');
          localStorage.removeItem('user');
          setApiKey(null);
          setCurrentUser(null);
        }
      } finally {
        if (!cancelled) setAuthChecked(true);
      }
    };
    verify();
    return () => { cancelled = true; };
  }, []);  // intentionally run once on mount

  useEffect(() => {
    if (!apiKey) return;
    const fetchPendingCount = async () => {
      try {
        const params = new URLSearchParams({ status: 'pending' });
        if (selectedId) params.set('community_id', selectedId);
        const res = await fetch(`${API_BASE}/api/applications/count?${params}`, {
          headers: { 'Authorization': `Bearer ${apiKey}` },
        });
        if (res.ok) {
          const data = await res.json();
          setPendingAppCount(data.count || 0);
        }
      } catch (e) { /* ignore */ }
    };
    fetchPendingCount();
    const interval = setInterval(fetchPendingCount, 30000);
    return () => clearInterval(interval);
  }, [apiKey, selectedId]);

  const getSelectedKey = () => {
    const path = location.pathname.replace('/', '') || 'dashboard';
    return path;
  };

  if (!authChecked) {
    return (
      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <LoadingOutlined style={{ fontSize: 32 }} />
      </div>
    );
  }

  if (!apiKey) {
    return <LoginForm onLogin={(t) => { setApiKey(t); setAuthChecked(true); }} />;
  }

  const menuItems = [
    { key: 'dashboard', icon: <DashboardOutlined />, label: 'Дашборд' },
    { key: 'communities', icon: <ApiOutlined />, label: 'Сообщества' },
    { key: 'users', icon: <TeamOutlined />, label: 'Пользователи' },
    { key: 'applications', icon: <SolutionOutlined />, label: <span>Заявки {pendingAppCount > 0 && <Badge count={pendingAppCount} size="small" style={{ marginLeft: 4 }} />}</span> },
    { key: 'tags', icon: <TagsOutlined />, label: 'Теги' },
    { key: 'matches', icon: <HeartOutlined />, label: 'Матчи' },
    { key: 'feedbacks', icon: <MessageOutlined />, label: '💬 Обратная связь' },
    { key: 'karma', icon: <TrophyOutlined />, label: 'Карма' },
    { key: 'moderation', icon: <SafetyOutlined />, label: 'Модерация' },
    { key: 'broadcast', icon: <SendOutlined />, label: '📧 Рассылка' },
    { key: 'logs', icon: <HistoryOutlined />, label: '📋 Логи' },
    { key: 'settings', icon: <SettingOutlined />, label: 'Настройки' },
  ];

  const handleMenuClick = ({ key }) => {
    navigate(`/${key}`);
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider collapsible collapsed={collapsed} onCollapse={setCollapsed}>
        <div style={{ height: 32, margin: 16, background: 'rgba(255,255,255,0.2)', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Text style={{ color: 'white', fontWeight: 'bold' }}>{collapsed ? '☕' : '☕ Random Coffee'}</Text>
        </div>
        <Menu
          theme="dark"
          selectedKeys={[getSelectedKey()]}
          mode="inline"
          items={menuItems}
          onClick={handleMenuClick}
        />
      </Sider>
      <Layout>
        <Header style={{ padding: '0 24px', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Space>
            <Title level={4} style={{ margin: 0 }}>Random Coffee Admin</Title>
            {communities.length > 0 && (
              <Select
                value={selectedId}
                onChange={selectCommunity}
                style={{ width: 220 }}
                options={communities.map(c => ({ value: c.id, label: `${c.name} ${c.bot_username ? '(@' + c.bot_username + ')' : ''}` }))}
              />
            )}
          </Space>
          <Space>
            <Tooltip title="Справка">
              <Button icon={<QuestionCircleOutlined />} onClick={() => setHelpVisible(true)} />
            </Tooltip>
            <Tooltip title="Выйти">
              <Button danger onClick={() => { localStorage.removeItem('apiKey'); setApiKey(null); }}>
                Выйти
              </Button>
            </Tooltip>
          </Space>
        </Header>
        <Content style={{ margin: 24 }}>
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<DashboardPage apiKey={apiKey} communityId={selectedId} />} />
            <Route path="/communities" element={<CommunitiesPage apiKey={apiKey} />} />
            <Route path="/users" element={<UsersPage apiKey={apiKey} communityId={selectedId} />} />
            <Route path="/applications" element={<ApplicationsPage apiKey={apiKey} communityId={selectedId} />} />
            <Route path="/tags" element={<TagsPage apiKey={apiKey} communityId={selectedId} />} />
            <Route path="/matches" element={<MatchesPage apiKey={apiKey} communityId={selectedId} />} />
            <Route path="/feedbacks" element={<FeedbacksPage apiKey={apiKey} communityId={selectedId} />} />
            <Route path="/karma" element={<KarmaPage apiKey={apiKey} communityId={selectedId} />} />
            <Route path="/moderation" element={<ModerationPage apiKey={apiKey} communityId={selectedId} />} />
            <Route path="/settings" element={<SettingsPage apiKey={apiKey} />} />
            <Route path="/botconfig" element={<Navigate to="/communities" replace />} />
            <Route path="/broadcast" element={<BroadcastPage apiKey={apiKey} communityId={selectedId} />} />
            <Route path="/logs" element={<LogsPage apiKey={apiKey} communityId={selectedId} />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Content>
      </Layout>
      <HelpDrawer visible={helpVisible} onClose={() => setHelpVisible(false)} />
    </Layout>
  );
}

export default App;